/**
 * The EN/ES dictionary and the one lookup helper allowed to index it.
 *
 * Ground Truth decision 0026: UI chrome is translated through this in-app dictionary; job-order content is
 * presented as stored, and there is no machine translation of application or legal text.
 *
 * Every key must exist in BOTH languages, in the same change. `EnDictionary` is the shape and `es` is typed
 * as `Record<TranslationKey, string>`, so a key added to English without a Spanish counterpart is a
 * TYPECHECK FAILURE rather than the literal text `undefined` on a screen in front of a candidate.
 */

export const LANGUAGE = { en: 'en', es: 'es' } as const;
export type Language = (typeof LANGUAGE)[keyof typeof LANGUAGE];

/** Render order of the language toggle. EN first — the Welcome screen defaults to English (KID-1). */
export const LANGUAGES: readonly Language[] = [LANGUAGE.en, LANGUAGE.es];

/**
 * The endonyms shown ON the language toggle, deliberately NOT in the dictionary.
 *
 * A language's own name does not change with the chrome language — "Español" reads the same to an English
 * speaker and a Spanish one, which is the whole point of an endonym on a toggle. Putting these in the EN/ES
 * dictionary would mean either duplicating each string across both languages (which the parity test in
 * `dictionary.test.ts` rejects, correctly) or translating a proper noun. They live here, in `src/i18n/`, so
 * the rule that matters — no user-facing string literal in a component (convention skill § 2) — still holds.
 */
/**
 * The company name, used as the logo's alt text.
 *
 * Outside the dictionary for the same reason as the endonyms below: it is a **proper noun**, identical in
 * English and Spanish, and the EN/ES parity test in `dictionary.test.ts` correctly rejects an entry whose
 * Spanish value equals its English one. Translating a company name would be the actual defect. It still
 * lives in `src/i18n/`, so the rule that matters — no user-facing string literal in a component (convention
 * skill § 2) — continues to hold.
 */
export const BRAND_NAME = 'Integrity Staffing Solutions';

export const LANGUAGE_LABEL: Record<Language, string> = {
  en: 'English',
  es: 'Español',
};

/**
 * What the toggle actually SHOWS [Figma · Language Selector, 11874:18814]: a flag and a two-letter code,
 * not the endonym.
 *
 * Same reasoning as `LANGUAGE_LABEL` for staying out of the dictionary — these are identical in both
 * languages, so the EN/ES parity test would reject them. `LANGUAGE_LABEL` remains the ACCESSIBLE name: a
 * screen reader announcing "E N" helps nobody, and "English" is what the control means.
 */
export const LANGUAGE_SHORT_LABEL: Record<Language, string> = {
  en: 'EN',
  es: 'ES',
};

const en = {
  // ── Fit questions · Figma `08 · Fit questions` — the CHROME only [03-us-kpw-3a AC-5] ──
  // The questions themselves are NOT here: they are ISS's stored set, read from `GET /screening/questions`
  // and rendered as authored in both languages [0026 § fit questions; KAD-1i]. The three placeholders and
  // the *Confirm your shift* row this block carried went with KPW-3a [D8].
  'fit.heading': 'Just a few quick checks',
  'fit.subtitle':
    "About the day-to-day for {job}. A 'no' won't rule you out — it just means a recruiter will follow up.",
  'fit.continue': 'Continue',
  // The read in flight, in the family of `jobs.loading` and `arrival.loading`.
  'fit.loading': 'Getting your questions ready…',
  // The set could not be read [AC-11, D10]: nobody was enqueued, so no person is promised. The heading is
  // KID-2's neutral hand-off, as `jobs.empty.unreachable.title` is — it is the same situation.
  'fit.unavailable.heading': 'Please see a representative',
  'fit.unavailable.body':
    "We couldn't load the questions just now. A member of staff can help you from here.",
  'fit.unavailable.done': 'Done',
  // The EEO / ADA note that closes the set [03-us-kpw-3a AC-13; decision 0367 (e)] — decision 0217's text,
  // byte for byte; the dash is a plain hyphen-minus. ⛔ LEGAL text: shown as stored, never
  // machine-translated [0328 rule 2; 0026], and where no stored Spanish exists the stored language is shown
  // [0104]. So the `es` entry IS this English text [0367 note 4] until [0366] rules; it is on `SAME_IN_BOTH`.
  'fit.eeoNote':
    'Note - Integrity complies with all applicable laws ensuring equal employment opportunities for individuals with disabilities and will make reasonable accommodation for a qualified individual with a disability, unless it results in undue hardship and/or a health or safety concern.',
  // ISS's own wording, from UAT [ISS-152; ISS owns candidate copy, 0040; Operator-approved 2026-10-05]. The
  // frame `10 · Submitted` still draws «Your application is in» — the design update is owed.
  'submitted.heading': 'Your interest in the job has been submitted.',
  'submitted.body':
    "You've applied for the {job} role. A recruiter reviews it next, and we'll confirm by email once your assignment is set.",
  'submitted.oneMore': "Before you go, there's one more thing to set up here at the kiosk.",
  'submitted.next': 'Next',
  'profileUpdate.heading': "Welcome back — let's update your profile",
  'profileUpdate.subtitle':
    "We've filled in what's already on file — just review and edit anything that's changed.",
  /*
   * ⭐ **This screen's phone label is "Phone", and the create form's is "Phone number".** Its own key
   * rather than `application.phone`, because the two frames genuinely disagree and the screen was
   * borrowing the other one's word: `04 · Returning profile` draws «Phone» at all three widths (Desktop
   * `12021:41053`, iPad `12021:41016`, Mobile `12021:40980`) while `04 · Application` draws «Phone number»
   * at all three (`12021:40603` / `40566` / `40530`) — both resolved by NAME and read live 2026-09-15.
   *
   * The authority is the **Operator's ruling of 2026-09-14** — *«Lo referido a titulos, subtitulos y demas
   * textos duros tomalos de figma»* — not the frame alone. No decision quotes either label, so nothing
   * verbatim is overridden. The three other fields on this screen's PERSONAL INFORMATION group (first
   * name, last name, email) are drawn identically on both frames and keep sharing `application.*`.
   */
  'profileUpdate.phone': 'Phone',
  'profileUpdate.preferences': 'Preferences',
  'profileUpdate.shiftPreference': 'Shift preference',
  'profileUpdate.anyShift': 'Any shift',
  'profileUpdate.dateAvailable': 'Date available',
  'profileUpdate.contact': 'Contact & emergency',
  'profileUpdate.homeAddress': 'Home address',
  'profileUpdate.emergencyContact': 'Emergency contact',
  'profileUpdate.emergencyPhone': 'Emergency phone',
  'profileUpdate.sensitive':
    'Direct deposit, pay card, and W-4 updates happen privately on your phone — never on this shared kiosk.',
  'profileUpdate.continue': 'Looks good, continue',
  /*
   * What the screen says when the save did NOT happen.
   *
   * It had no words for that state at all: a refused write moved the candidate on to the arrival menu, so
   * they left believing a correction that never landed had been saved (observed on dev, 2026-09-07). The
   * summary is deliberately general — the api's own message is untranslated English written for a
   * developer — and the field messages beside it say which control to look at.
   */
  'profileUpdate.saveRejected':
    "We couldn't save your changes — please check the highlighted fields and try again.",
  'profileUpdate.fieldRejected': 'Please check this field.',
  /*
   * **Each of these says what the field needs AND that empty is allowed** [decision 0172 rule 1].
   *
   * The second half is not politeness: on this screen these values arrive PRE-FILLED from Bullhorn, so the
   * candidate is being asked to fix something they never typed. Clearing it is the fastest way out, it is
   * always accepted, and nothing on the record is lost by it — an empty field is omitted from the write
   * rather than blanking what is stored.
   */
  'profileUpdate.phone.error': 'Enter a 10-digit phone number, digits only — or leave it empty.',
  'profileUpdate.payRate.error': 'Enter an hourly rate like 18.50 — or leave it empty.',
  'profileUpdate.dateAvailable.error': 'Choose today or a later date — or leave it empty.',
  'application.heading': "Let's create your profile",
  'application.subtitle': 'Just the basics to get you matched — takes about 2 minutes.',
  'application.personal': 'Personal information',
  'application.firstName': 'First name',
  'application.firstName.error': 'First name is required.',
  'application.lastName': 'Last name',
  'application.lastName.hint': 'Pre-filled from your lookup — edit if needed.',
  'application.lastName.error': 'Last name is required.',
  'application.email': 'Email',
  'application.email.error': 'Please enter a valid email address.',
  'application.phone': 'Phone number',
  'application.phone.error': 'Enter a 10-digit phone number — digits only.',
  'application.location': 'Location',
  'application.city': 'City',
  'application.city.error': 'City is required.',
  'application.state': 'State',
  'application.state.error': 'Please select a state.',
  'application.zip': 'ZIP',
  'application.zip.error': 'Enter a valid 5-digit ZIP.',
  'application.employment': 'Employment details',
  'application.payRate': 'Desired Pay Rate',
  'application.payRate.hint': 'Enter your desired hourly rate.',
  'application.payRate.error': 'Please enter a valid pay rate.',
  'application.startDate': 'When are you able to start?',
  // The frame's hint under the pre-filled surname: it arrives from the check-in factors, and somebody whose
  // name is spelled differently on their id needs to know they may change it.
  // The calendar's own chrome. Month and weekday NAMES are not here — they come from `Intl` in the selected
  // language, because a hand-kept list of twelve months in two languages is twenty-four chances to be wrong
  // about something the platform already knows.
  'calendar.open': 'Open the calendar',
  'calendar.close': 'Close the calendar',
  'calendar.previousMonth': 'Previous month',
  'calendar.nextMonth': 'Next month',
  'application.startDate.error': 'Please enter the date you can start.',
  'application.workAuthorized': 'Are you legally authorized to work in the US?',
  'application.isAdult': 'Are you 18 years of age or older?',
  'application.yes': 'Yes',
  'application.no': 'No',
  'application.answerRequired': 'Please answer this question.',
  'application.sensitive':
    'Sensitive details like your SSN and direct deposit are completed privately on your own phone — never on this shared kiosk.',
  // ── 0160 · the consent modal · Figma `04a · Consent to contact (dialog on Continue)` ──
  // The modal's own title, and the same string the profile screen used as its section heading before ISS
  // moved the consent off that screen.
  'application.consentHeading': 'Consent to contact',
  'consent.review': 'Please review the information below before continuing.',
  /*
   * ISS's copy, reproduced VERBATIM from `Overlay · Consent to contact — Desktop` (12389:15546).
   *
   * ⚠️ **It opens differently at the three viewports and the Desktop wording is the one used.** Desktop
   * says *"By selecting "Agree""*; iPad and Mobile still say *"By checking this box"*, which is the
   * inline-checkbox wording [0156] and describes a control the modal does not have. [0040] reserves this
   * copy to ISS, so the divergence is reported to them rather than reconciled here — but one of the two had
   * to be shown, and only one of them is true on the screen it is drawn on.
   */
  'application.consentFull':
    'By selecting “Agree”, you give Integrity Staffing Solutions and its affiliates, and contracted partners permission to contact you about job openings using calls, AI-generated calls, text messages, or emails. When submitting the application, please ensure that the telephone number provided is one you are permitted to use and are authorized to receive calls, AI-generated calls, text messages, or emails on. If your phone number changes during the application process, please inform Integrity promptly. You can opt-out of receiving these communications at any time by saying or entering STOP. For help, enter HELP. Frequency varies for text messages. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages. You can access our privacy policy at https://integritystaffing.com/privacy-policy-guidelines. * This application includes a binding Arbitration Agreement that requires you and Integrity Staffing to arbitrate covered claims, including, without limitation, claims arising out of your application, and employment (if you are hired), which will be decided by a single arbitrator through final and binding arbitration and not by court or jury trial. *',
  'consent.question': 'Do you consent to be contacted?',
  'consent.agree': 'I agree',
  'consent.decline': 'I do not agree',
  /*
   * ── 0161 / 0195 · the three refusals · Figma `STATES & OVERLAYS` → `Blocked — cannot continue` ──
   *
   * ISS's copy, read verbatim from `Blocked · consent` / `· age` / `· auth` on 2026-09-10. Heidi Carrel
   * offered the career portal's existing wording for the two eligibility explainers [0161, 12:01] and the
   * frames now carry it: **do not draft legal explanation here.** Each screen says what happened, why, and
   * what the candidate can do — the third part is what keeps it from being the dead end BR-K7 forbids.
   *
   * ⛔ **ISS-201 [0161]: the age and auth `whatToDo` lines lost their FIRST sentence — nothing was added.**
   * They read *"If you entered this by mistake, go back and change your answer"* / *"If you answered by
   * mistake, go back and change it"*, which told the candidate to take a way back that 0161 forbids and the
   * screen no longer draws. Only the deletion is ours; every word left is the frame's. (`Otherwise, come`
   * became `Come`, the capital the sentence needs once the one before it is gone.)
   */
  'blocked.consent.heading': 'We can’t continue without your consent',
  'blocked.consent.body':
    'We need your permission to contact you about jobs — without it we have no way to tell you about openings or next steps.',
  'blocked.consent.whatToDo':
    'If you change your mind, go back and choose “I agree”. Nothing you typed has been lost.',
  'blocked.age.heading': 'You need to be 18 or older',
  'blocked.age.body': 'The roles we place through this kiosk require you to be at least 18.',
  'blocked.age.whatToDo': 'Come back and see us once you turn 18 — we’d be glad to help then.',
  'blocked.auth.heading': 'We can’t continue without work authorization',
  'blocked.auth.body':
    'To place you in a role we need you to be authorized to work in the United States.',
  'blocked.auth.whatToDo': 'If your situation changes, you’re welcome to apply again.',
  'blocked.goBack': 'Go back and change my answer',
  'blocked.exit': 'Exit',
  'application.continue': 'Continue',
  'application.saveRejected':
    "We couldn't save your details — please check the highlighted fields and try again.",
  // Figma · 01 · Welcome. The frame says "Welcome to Integrity", not a bare "Welcome".
  'welcome.title': 'Welcome to Integrity',
  /*
   * The frame's sentence, em dash and all, read live from `Welcome (tap to start) — Desktop` /
   * `— iPad` / `— Mobile` (identical at all three) on 2026-09-14.
   *
   * ⚠️ **This reverses what stood here.** The note this replaces kept the dash out on the rule that
   * *frame text is illustrative, and where they differ the decision wins*
   * [04-eng-engineering-context/design-system § *Figma is not the oracle for these*]. **The Operator
   * ruled otherwise on 2026-09-14** — *«los textos de figma estan validados… Lo referido a titulos,
   * subtitulos y demas textos duros tomalos de figma»* — so headings, sub-headings and other hard text
   * now come from the frame. The authority for this string is HIM, not the frame on its own; the
   * design-system record is being amended separately. Spanish mirrors the structure in the same change.
   */
  'welcome.subtitle': 'Apply for warehouse and logistics jobs near you — in just a few minutes.',
  'welcome.languageLabel': 'Language',
  'welcome.tapToStart': 'Tap to start',
  // ── KID-1 · 02 · Have you applied? · Figma 12055:10222 desktop / 10237 iPad / 10252 mobile ──
  'applied.heading': 'Have you ever applied with us?',
  'applied.subtitle':
    'Even if you never worked a shift — applying is enough for us to already have your record.',
  /*
   * The two doors, verbatim from **[decision 0125]** — not from the frame. Copy is governed by [0026] and
   * [0040] and frame text is illustrative; what makes these the strings to build is that 0125 is
   * `approved`, records them character for character in its own Decision section (*"Build these strings
   * verbatim"*) as validated by Heidi Carrel, and is the newest non-superseded decision on them. The
   * frames and the prototype happen to agree, which is corroboration and not the authority.
   *
   * ⚠️ [0070] rejected the "worked" clause by name and handed the final string to ISS; ISS then supplied
   * one that keeps it. 0125 records that as a change of position rather than a rule being satisfied, and
   * says 0070 is OWED an update — raised, not resolved here.
   *
   * The apostrophe is U+2019, as 0125 writes it.
   */
  'welcome.newApplicant': 'I’ve never applied with Integrity before.',
  'welcome.returning': 'I’ve previously applied/worked at Integrity.',
  // The check-in headings are the ONLY thing the door choice changes (KID-1). Nothing routes on them.
  'checkIn.heading.new': "Welcome! Let's get you started",
  'checkIn.heading.returning': 'Welcome back!',
  'checkIn.email': 'Email',
  'checkIn.lastName': 'Last name',
  'checkIn.birthDay': 'Day of birth',
  'checkIn.birthMonth': 'Month of birth',
  'checkIn.ssnLast4': 'Last 4 of SSN',
  'help.fab': 'Need help?',
  'badge.kiosk': 'Kiosk',
  // The top bar's session-ending control and the overlay it opens [Figma · `Exit button`; `States / 01 ·
  // Overlay · Exit — clear this session?`, Desktop · iPad · Mobile].
  'exit.control': 'Exit',
  /*
   * The overlay's three strings, verbatim from ISS [decision 0340 — Heidi Carrel, in writing, 2026-09-23],
   * and the live frame now carries the same text. It says SAVED, not erased: [0199] established that
   * nothing is erased on either side of the three gates, so the earlier promise of erasure was false, and
   * this one wording is true before the gates and after them.
   *
   * No trailing full stop on the disclosure, and the two answers in capitals, because that is how ISS typed
   * them and how the frame draws them — a tidied version would be a string nobody approved [0040].
   */
  'exit.heading': 'Are you sure you want to exit now?',
  'exit.body': 'Information submitted so far will be saved in our applicant tracking system',
  'exit.keepGoing': 'NO',
  'exit.confirm': 'YES',
  // Figma · 02 · Check-in — read from the frame, not paraphrased.
  'checkIn.subheading':
    "We'll ask for a few details to get you started. If we already have a record for you, we'll pick up right where you left off.",
  'checkIn.emailPlaceholder': 'name@example.com',
  /*
   * The frame's label, in the frame's order [`02 · Check-in — Desktop` / `— iPad` / `— Mobile`, read live
   * 2026-09-14; all three read "(month & day)"]. It said "(day & month)" until the Operator's ruling of
   * 2026-09-14 put hard text back on the frame.
   *
   * The two selects render Month then Day, the label's order [0301], and the frames now draw the same
   * order (re-read live 2026-09-30, all nine check-in frames). What is collected is unchanged: day and
   * month, never the year [decision 0032].
   */
  'checkIn.dob': 'Date of birth (month & day)',
  // The frame's own words [12446:28586]. It names both halves, because the candidate can only see which
  // one they left alone by reading it.
  'checkIn.dobBothRequired': 'Please select both the day and the month.',
  // The other three fields' messages, the frame's own words too: `New user / 03 · Check-in` carries each in
  // the field's hidden `Validation message` slot at all three widths (read live 2026-09-30) [ISS-147, 0297].
  'checkIn.emailInvalid': 'Please enter a valid email address.',
  'checkIn.lastNameRequired': 'Last name is required.',
  'checkIn.ssnLast4Invalid': 'Enter the last 4 digits of your SSN.',
  'checkIn.dobDay': 'Day',
  'checkIn.dobMonth': 'Month',
  // Figma · 03 · Routing is the LOADING state, not an outcome screen.
  'routing.checking': 'Checking your record…',
  'checkIn.back': 'Back',
  'lookup.submit': 'Continue',
  // KID-2 · the four-factor rule, stated to the candidate on the screen that applies it.
  'checkIn.allFourRequired':
    'We use this complete set of details together to confirm your identity — please fill in all four.',
  'lookup.pending': 'Checking your details…',

  // KID-2 · the diagnosis outcomes. Each names what happens NEXT, because that is what the candidate needs.
  'routing.new.heading': "Let's start your application",
  'routing.new.body': "We didn't find an existing record, so we'll set one up now.",
  'routing.returning.heading': 'Welcome back',
  'routing.returning.body': "We found your record. Let's pick up where you left off.",
  'routing.resume.heading': 'Welcome back',
  'routing.resume.body':
    "You've already started with us. We'll continue that application rather than begin a new one.",
  'routing.activePlacement.heading': "You're currently placed",
  'routing.activePlacement.body': 'Your assignment details are in your portal.',
  'routing.continue': 'Continue',

  /**
   * The neutral hand-off. It states NO reason — not ambiguity, not a barred record, not an outage — because
   * the same words appear for all of them, on a screen in a public lobby.
   */
  // ── 04 · See a rep · Figma `04 · See a rep — Desktop` / `— iPad` / `— Mobile`, resolved by NAME and
  // read live 2026-09-14. Identical at all three widths.
  //
  // ⚠️ **Two of the three strings below — the badge and the heading — are the frame's own, and they replaced
  // copy this build had written** (the body is 0504's, below). The
  // screen was rebuilt to this frame on 2026-09-02 but kept the Ground Truth's words, on the rule that
  // *frame text is illustrative*. **The Operator reversed that on 2026-09-14** — *«Lo referido a titulos,
  // subtitulos y demas textos duros tomalos de figma»* — and HE is the authority here, not the frame on
  // its own.
  //
  // ✅ **The hand-off is still neutral.** The heading and body state no reason: not ambiguity, not a
  // barred record, not an outage. The reason line below is the candidate's OWN declaration from the
  // arrival menu and is absent when there is none [decision 0001].
  //
  // ⭐ **The body is the Operator's ruling, not the frame's** [decision 0504 point 1; 03-us-kid-7a]. It
  // promises a recruiter and no call *by name*, which the entry raised on arrival [0500] may not carry. The
  // frame still draws the old sentence; copy follows the Ground Truth, and the frame is owed an update.
  'seeARep.badge': 'Rep',
  'seeARep.heading': 'Please have a seat',
  'seeARep.body': 'A recruiter will be with you shortly.',
  'seeARep.reason': 'You’re here for: {reason}',
  'seeARep.documents':
    'Please have your employment authorization documentation available for review.',
  'routing.assistance.heading': 'Please see a representative',
  'routing.assistance.body': 'A member of our team at the front desk can help you check in.',
  'routing.assistance.startOver': 'Start over',

  // KPW-1c · E4 · the Bullhorn write failed. ISS's English, character for character, from
  // `Edge cases / E4 · Submission failed (Bullhorn / Workato) — Desktop` (nodes 12234:26091, 26092 and the
  // `Primary` label), read live 2026-10-08 [0040; 0155]. Straight apostrophe as drawn. ⛔ Nothing here may
  // speak for Workato — no welcome email, no onboarding, no "ISS was notified" [0074; OQ-77].
  'submissionFailed.heading': "We couldn't send your application",
  'submissionFailed.body':
    'Something went wrong on our side — nothing you did. Your details are saved. A representative will finish this with you.',
  'submissionFailed.getHelp': 'Get help',

  // KID-7 · a sign-in dead end's own help button [0230; the first scenario]. No frame draws it, so it reuses
  // the words of the one-tap control the app already ships (`submissionFailed.getHelp`, ISS's `Primary`
  // label on E4) rather than inventing copy [0040]. Recorded in `design-qa/known-departures.md`.
  'deadEnd.getHelp': 'Get help',

  // KID-2 · the throttle. Reveals RATE, never match status — the one permitted departure from the neutral
  // hand-off, and only because a wait discloses nothing about whether a record was found.
  'throttle.heading': 'Please wait before trying again',
  'throttle.body':
    "We've had several unsuccessful attempts with these details. You can try again in:",
  'throttle.recruiter': 'If this keeps happening, please ask a member of our team for help.',
  'throttle.tryAgain': 'Try again',
  'throttle.remaining': 'Time remaining',

  // KID-2 · the different-email prompt — `Email / 02 · Email dialog` [12021:41256 / 41254 / 41252, read live
  // 2026-09-30], the frame's text at all three widths [0297]. `{email}` is the frame's sample
  // `maria.gomez@example.com`, i.e. the address on file. It offers the choice 0030 and KID-2 describe —
  // keep the new email (Continue) or use the one on file — and never overwrites silently or duplicates.
  'differentEmail.heading': 'We found your account',
  'differentEmail.body':
    'Your account is currently under {email}. The email you just entered is different — fix it below if it was a typo, or continue to keep the new one.',
  'differentEmail.emailLabel': 'Your email',
  'differentEmail.continue': 'Continue',
  'differentEmail.useOnFile': 'Use my account email instead',
  'error.generic': 'Something went wrong. Please ask a member of staff for help.',

  // ── Screens after the diagnosis. One heading each; the stories that own them add the rest. ──
  // Every English string below is Figma's own text, read from the live page 12021:35970 on 2026-08-19.
  // KID-5 · Figma 12195:11555
  'arrival.heading': 'What brings you in today?',
  // The menu waits for the two record reads before it renders a row [D2 / defect fix 2026-09-11]. Chrome,
  // in the family the other three waits already use — `routing.checking`, `jobs.loading`,
  // `associate.loading` — and it names no record, no status and no compliance flag, because this screen is
  // read over the candidate's shoulder in a lobby.
  'arrival.loading': 'Getting your options ready…',
  // ── KJM-1 · 06 · Jobs · Figma 12021:40673 desktop / 40656 iPad / 40640 mobile ──
  'jobs.heading': 'Open positions near you',
  // ⚠️ OWED COPY, not from a frame. The frame draws "Jobs near your ZIP (18101) — closest first.", and
  // decision 0158 (approved) retires both halves: the list is ranked by the recruiter's manual position
  // alone, so "closest first" is untrue, and the payload carries no candidate ZIP to name — the old build
  // named an order's WORKSITE ZIP instead [ISS-141]. This is the sentence the no-ZIP path already showed,
  // now shown to everyone; it is Rootstrap's, and decision 0040 makes candidate-facing copy ISS's.
  // Replace on their word. The previous version cited a PO ruling of 2026-09-05 (prototype walkthrough)
  // keeping "closest first"; that ruling is not in the Ground Truth, so approved decision 0158 governs.
  'jobs.subheading': 'Every opening this office is hiring for right now.',
  'jobs.featured': 'Featured',
  // A title is nullable in Bullhorn. Showing a blank card is worse than a generic one.
  'jobs.untitled': 'Open position',
  'jobs.loading': 'Finding jobs near you…',
  // The `Get help` callout the frame draws under the list [Figma 12055:10340 desktop / 10357 iPad /
  // 10374 mobile]. English is the frame's own text, VERBATIM, including the arrow in the control's label —
  // built on the Operator's ruling of 2026-09-02. It is candidate-facing copy ISS has not yet signed off
  // (OQ-58), so it is theirs to replace on their word, exactly like `jobs.subheading` above.
  'jobs.help.title': 'Not sure, or just have a question?',
  'jobs.help.body':
    "Help is available the whole time you're here — from a quick AI chat to a real person if you need one.",
  'jobs.help.action': 'Get help →',
  // ── KJM-1 · 07 · No jobs · Figma 12021:40729 desktop / 40709 iPad / 40690 mobile ──
  // The empty result is its own screen, with its own words.
  'jobs.empty.heading': 'No exact match right now',
  'jobs.empty.body':
    "We don't have an opening that fits you at this location today. Here's how we keep you moving:",
  // ⚠️ The frame offers TWO choices — a "keep me on the list" capture and "talk to a recruiter". Decision
  // 0087 retired the capture (an Evergreen is now an ordinary order in the pool, not a fallback) and 0095
  // makes the recruiter queue the single first-line hand-off, which the BACKEND already performed before
  // this screen rendered. So there is one block and it reports rather than offers. The second sentence is
  // the frame's own, verbatim; the title is Rootstrap's, because the frame has no words for "already done".
  'jobs.empty.recruiter.title': 'A recruiter is on the way',
  'jobs.empty.recruiter.body': 'A recruiter can look wider and go over your options with you.',
  // The transport failed, so nobody was enqueued and the screen may not promise a person. Same words as
  // KID-2's neutral hand-off, deliberately: it is the same situation.
  'jobs.empty.unreachable.title': 'Please see a representative',
  'jobs.empty.unreachable.body':
    "We couldn't load the openings just now. A member of staff can help you from here.",
  'jobs.empty.done': 'Done',
  // ── KJM-3 · 08 · Job detail · Figma 12021:40798 desktop / 40773 iPad / 40749 mobile ──
  // The frame's visible title is the job's own, which is data rather than copy; this is the stable heading a
  // screen reader lands on when focus moves to the opened card.
  'jobs.detail.heading': 'Job details',
  // The five summary rows, in the frame's order.
  'jobs.detail.shift': 'Shift',
  'jobs.detail.pay': 'Pay',
  'jobs.detail.jobType': 'Job type',
  'jobs.detail.location': 'Location',
  'jobs.detail.jobId': 'Job ID',
  // Decision 0072 retitled this section. It was "Essential duties and responsibilities", which was wrong
  // because the block is a pass-through and a parsed block's label invites the parsing 0051 forbade.
  'jobs.detail.fullDescription': 'Full job description',
  'i9.heading': 'Finish your I-9',
  // ISS rewrote this screen in the design file: one sentence, then the two parts as a numbered list, then
  // one callout. The USCIS-compliance paragraph is gone from their own copy — what it explained (an
  // Authorized Representative may view the documents and complete Section 2) survives in `i9.section2`.
  // 0040 reserves candidate-facing copy to ISS, and the frame IS ISS's; the drop is reported, not decided.
  'i9.body': 'Your I-9 is a federal work-authorization form in two parts.',
  'i9.section1':
    'Section 1 is yours. Complete it in your Talent Portal, on your phone or computer.',
  // ⚠️ The frame's version of this part offered a REMOTE Authorized Representative — "That can be anyone
  // over 18 you trust, from any computer or phone. They don't need to be here." Decision 0050 rules the
  // opposite in its own words: "I-9 Section 2 remains in person with an authorized representative (0004)",
  // and it retired the "I'll do the rest later with an authorized representative" choice outright. An
  // approved decision is not overridden by a frame that has not caught up (skill § 8), so the two remote
  // sentences are gone and "in person" is 0050's own wording, not new copy. OQ-94 owns the live question —
  // whether ISS wants that route back — and the frame owes an update either way.
  'i9.section2':
    'Section 2 must be done in person by an Authorized Representative — you cannot complete it yourself.',
  'i9.staffCanHelp':
    'If you have your work authorization documents with you today, one of our staff can complete Section 2 with you now.',
  // ⭐ The question and BOTH answers, verbatim from `12 · I-9 info — Desktop` (read live 2026-09-11). They
  // were removed on a reading of 0050 and the Operator asked for them back on 2026-09-11: "La llamada para
  // i9 a la queue solo debe darse si toco en el boton yes, help me today. Por eso hacen falta los dos
  // botones." The queue entry belongs to the button, so the button has to exist [decision 0206].
  //
  // ⚠️ The compliance half — whether "someone else" may be a friend or family member acting as Authorized
  // Representative, which 0050 retired — is OQ-94 + OQ-111 and is NOT settled by restoring these. The copy
  // is ISS's either way [0040]: taken from the frame, not written here.
  'i9.assistQuestion': 'Would you like a staff member to help you today?',
  'i9.helpYes': 'Yes, help me today',
  'i9.helpNo': 'No, someone else will',
  // ⭐ ISS's sentence, as Heidi Carrel typed it on 2026-09-23 [decision 0341], and as `03 · I-9 info` draws it
  // (`Acceptable documents — USCIS link`, read live 2026-10-01 — frame and 0341 agree character for
  // character). ⛔ Ships verbatim: not re-punctuated, not tightened, not split [0359 ruling 3]. The control is
  // `i9.documents.linkLabel`, found INSIDE this string by `DocumentsSentence`, so the two must agree —
  // `dictionary.test.ts` holds them to it. It characterises a federal requirement, so it is shown AS STORED
  // on a Spanish kiosk [0359 ruling 2; 0328 rule 2]: the `es` entries below are this English, on
  // `SAME_IN_BOTH`, until ISS supplies the Spanish.
  'i9.documents.sentence':
    'To complete your Form I-9, you must provide documentation to confirm your identity and authorization to work. If you need additional guidance on what documentation can be used for your Form I-9, please click here.',
  'i9.documents.linkLabel': 'please click here',
  // The overlay *please click here* opens [decision 0370], verbatim from `03 · I-9 — USCIS documents QR … —
  // Desktop` / `— iPad` / `— Mobile` (identical at all three, read live 2026-10-01). The caption keeps the
  // frame's straight apostrophe. ⚠️ Design's copy on a frame whose own note says *"Overlay copy is
  // candidate-facing → ISS sign-off [0040]"* — so it is owed ISS's confirmation, like `exit.*`.
  'i9.documentsQr.title': 'Form I-9 acceptable documents',
  'i9.documentsQr.body': 'Scan this code to open the official USCIS list on your own phone.',
  'i9.documentsQr.caption': "Scan with your phone's camera",
  'i9.documentsQr.close': 'Close',
  /*
   * ⭐ **The control that writes the Bullhorn submission, relabelled to the frame.**
   * `New user / 07 · Job detail` draws its primary action as *"Yes, I’m interested in this job."* at all
   * three widths (Desktop `12055:10391`, iPad `12055:10416`, Mobile `12055:10441`, resolved by NAME and
   * read live 2026-09-14); this said *"Select this position"*, which appears nowhere in the design file.
   *
   * The authority is the **Operator's ruling of 2026-09-14** — *«Lo referido a titulos, subtitulos y demas
   * textos duros tomalos de figma»*, button labels included — not the frame by itself. KJM-3 names only
   * *"the primary select action"* and fixes no wording, and no decision quotes either string, so nothing
   * verbatim was overridden. The apostrophe is U+2019, as the frame writes it.
   *
   * ⚠️ **The frame says MORE than the button.** Directly above it, `New user / 07 · Job detail` draws
   * two sentences that tell the candidate the tap SUBMITS. Without them the new label states interest where
   * the old one stated an act. They were left out of the relabel because they are new elements and not a
   * copy fix. KJM-3a AC-8 now renders them: `jobs.detail.sendsApplication` and `jobs.detail.contingencies`,
   * below.
   */
  'jobs.detail.select': 'Yes, I’m interested in this job.',
  // No frame draws the in-flight label — the frame has one state for this button — so this stays as it is.
  'jobs.detail.selecting': 'Sending your application…',
  /*
   * The way back to the list from the bottom of the description [decision 0305]. The frame's own text,
   * verbatim: the `Secondary` instance's `Label` property in `New user / 07 · Job detail` — Desktop, iPad
   * and Mobile all carry it, read live 2026-10-01. 0305 calls Jaime Donnelly's *"let me look for another
   * one or something"* an example and not copy, and leaves the label to the frame [0297]. The frame
   * settled on that same wording, minus *"or something"*.
   */
  'jobs.detail.lookForAnother': 'Let me look for another one',
  /*
   * ⭐ **What selecting does, and what can come after it** [KJM-3a AC-8; BR-K9; 0003]. ISS's text, verbatim
   * from `New user / 07 · Job detail`. Desktop draws it as nodes `12441:27917`/`27918`, iPad as
   * `27919`/`27920` and Mobile as `27922`/`27923`, and all three carry the same strings (read live
   * 2026-10-06) [0297; 0040]. The first sentence is ISS's replacement for the prototype's old *"then you're
   * placed"* promise [0055 #1]: the tap sends an application and books nothing. The second is how the frame
   * names the contingencies. It is generic on purpose, so no per-order field is read for it.
   */
  'jobs.detail.sendsApplication':
    'Selecting this position sends your application. A couple of quick questions follow.',
  'jobs.detail.contingencies':
    'Some clients require a background check and/or drug screen before you start.',
  // KAS-1 · Figma 12021:41206 — the frame reads "Welcome back, Maria"; the name is data, not copy.
  'associate.heading': 'Welcome back',
  // KPC-1 · Figma 12021:41960
  'payCard.heading': 'Link your pay card',
  // KAP-2 / KAP-5 · Figma 12021:40922
  'finish.heading': 'Your next steps',

  // The five-step journey rail [Figma · Stepper 12084:26382]. Shared by the pay-card and Finish screens,
  // so the labels live here rather than in either feature.
  'stepper.label': 'Your progress',
  'stepper.findJob': '1 · Find a job',
  'stepper.questions': '2 · Questions',
  'stepper.submitted': '3 · Submitted',
  'stepper.getSetUp': '4 · Get set up',
  'stepper.allSet': '5 · All set',
  // At 375 the design draws a RING instead of that rail [Figma · `Rail (mobile)`, e.g. 12469:14808 on
  // `09 · Submitted — Mobile`, read live 2026-09-15]. It says the position INSIDE the circle — «3 of 5» —
  // with a BARE label beside it and the step that follows underneath. The numbered labels above stay with
  // the rail they belong to: repeating the number next to a ring that already draws it is noise.
  'stepper.ringProgress': '{current} of {total}',
  'stepper.next': 'Next: {label}',
  'stepper.findJob.bare': 'Find a job',
  'stepper.questions.bare': 'Questions',
  'stepper.submitted.bare': 'Submitted',
  'stepper.getSetUp.bare': 'Get set up',
  'stepper.allSet.bare': 'All set',
  // ── KID-5 · "What brings you in today?" — the arrival-reason menu ──
  // Every English string below is the frame's own text, read verbatim from
  // Figma 12195:11555 (iPad) on 2026-08-19 — including the em dash in the last option and the curly
  // apostrophe in the first sub-label. ⚠️ The candidate-facing wording is ISS's to supply [decision 0040]
  // and KID-5 § Round-2 records it as owed; this is the DESIGN's copy, which is the nearest thing to an
  // oracle that exists, and it is superseded the moment ISS sends theirs.
  //
  // ⚠️ **There is one string per row, and there used to be two.** Seven `.action` keys sat here between
  // 2026-08-24 and 2026-09-10 — `See my assignment`, `See my application`, `See open jobs`,
  // `Get the details`, `Finish my I-9`, `Get a pay card`, `Talk to someone` — for a button the frame does
  // not draw. **None of the seven appears anywhere in the design file, at any of the three widths.** With
  // no element in the frame there is nothing for § 8 to derive them from, so they were written by the
  // build: seven strings put in front of a stranger that neither the design nor ISS supplied. Deleted
  // rather than reworded — the row's title is the affordance, and there is no second element to translate.
  // (The note above is the accurate reading of 0040 for this screen: the wording is owed from ISS and
  // ⚠️ 0040 does not itself reserve copy authorship — it bars internal verbiage and fixes the success
  // screen's contents. The § 8 argument is what carries the deletion.)
  //
  // **`reconfirmAssignment` is the one row no frame DRAWS** — Figma carries it as a `visible: false` layer,
  // which is how the design expresses a condition [0198] but not something you can read a treatment off.
  // Its copy is the updated prototype's (`ISS Kiosk updated — Happy Path.html`, `#hubSeeInfo`, the
  // returning user who has a placement), and the English matches this dictionary verbatim, which is the
  // check that mattered [Operator, 2026-09-10]. Recorded in the Ground Truth on `03-us-kid-5`.
  // ⚠️ Not adopted from the prototype: its **«usted»** register. This column is «tú» throughout, so one
  // «usted» row would read as a different voice to the candidate; the register is a dictionary-wide
  // question and ISS's to settle [0026], not this row's.
  //
  // `today` is likewise the frame's word, dropped at some point and restored 2026-09-10 —
  // 12195:11508 / 11555 / 11602 all read "Pick what you came in for today."
  'arrival.subheading': 'Your information is up to date. Pick what you came in for today.',
  'arrival.reconfirmAssignment.title': 'Reconfirm my assignment information',
  'arrival.reconfirmAssignment.subtitle': 'Where you’re going, your shift, and your first day',
  'arrival.seeMyApplications.title': 'See what I applied for',
  'arrival.seeMyApplications.subtitle': 'See where your application stands',
  'arrival.applyForJob.title': 'Search for a job',
  'arrival.applyForJob.subtitle': 'See open positions near you',
  'arrival.onboardingPaperwork.title': 'Complete my onboarding paperwork / I-9',
  // ⚠️ The frame's subtitle led with "Your Form I-9 Section 2 can be completed by a friend or family member
  // as our Authorized Representative" — the route decision 0050 retired (OQ-94). That offer is removed.
  // ⚠️ **The replacement is RE-AUTHORED, not a pure deletion, and that is worth being honest about.** The
  // frame's remaining sentence is "Or we can help you complete this today"; deleting the first sentence
  // leaves its "Or" dangling and its "this" without an antecedent, so the object is restated and 0050's
  // "in person" added. Every word comes from the frame or from 0050 § Decision, and no new claim is made —
  // but 0040 reserves this copy to ISS, so it is INTERIM. The exact string is recorded in OQ-94 for ISS to
  // review, and it is replaced by their wording when the frame is redrawn.
  'arrival.onboardingPaperwork.subtitle':
    'We can help you complete your Form I-9 Section 2 today, in person.',
  'arrival.drugTest.title': 'Complete my drug test',
  'arrival.drugTest.subtitle': 'Drug tests would most likely be completed at the hiring event.',
  'arrival.payCard.title': 'Link pay card',
  'arrival.payCard.subtitle': 'Set up how you get paid',
  'arrival.somethingElse.title': 'Ask for help from a representative',
  'arrival.somethingElse.subtitle': 'Someone on site will come and help you',

  // KAS-1 / KAS-2 · Figma `14 · Active associate — Desktop` / `— iPad` / `— Mobile`, read live 2026-09-14.
  //
  // The destination is the **talent portal** and never another form [decision 0071] — that NAME is
  // unchanged and is what 0071 rules. What changed below is only its CAPITALISATION: the frame writes
  // *Talent Portal*, this file wrote *talent portal*, and the note here used to resolve that the other way
  // ("the specs win"). **The Operator ruled on 2026-09-14 that hard text comes from the frame**, so the
  // frame's casing is taken. It also ends a three-way split inside this app — `i9.section1`,
  // `payCardLinked.activate` and `finish.intro` already carry ISS's *Talent Portal*, which
  // is the exact class of inconsistency decision 0080 flagged on ISS's own copy.
  'associate.subheading': 'Here are your assignment details.',
  'associate.subheading.applied': 'Here is what you applied for.',
  // The plural form. A placement is always alone [BR-K6], so a list is only ever applications — which is
  // why this says applications and not assignments.
  'associate.subheading.applications': 'Here are the {count} jobs you applied for.',
  'associate.application': 'Application {n}',
  'associate.client': 'Client',
  'associate.location': 'Location',
  'associate.shift': 'Shift',
  'associate.nextShift': 'Next shift',
  'associate.firstDay': 'First day',
  'associate.portalDeflection.title': 'Need to update pay, tax, or personal info?',
  'associate.portalDeflection':
    "This happens in your Talent Portal, which can be accessed on your phone or laptop. If you don't remember how to access your Talent Portal, let us know and we'll notify an Integrity rep that you need assistance.",
  'associate.portalDeflection.action': "I can't access my Talent Portal →",
  'associate.portalDeflection.done': "We've let a representative know.",
  'associate.done': 'Done',
  'associate.loading': 'Looking up your assignment…',

  // KAP-5a · Figma `New user / 12 · Your next steps` (was `10a`), read live 2026-10-05 from the prominence
  // frames 12860:29119 / 29199 / 29279 — every English string below is the frame's `characters`, verbatim.
  // ⭐ **The whole screen is the app's own copy now, so all of it is here** [decision 0436; 0328 rule 1]: its
  // Spanish is machine-translated and ISS REVIEWS it (OQ-134, open) — it is not application or legal text,
  // so the no-machine-translation rule of `src/i18n/CLAUDE.md` does not reach it. It used to live outside
  // the dictionary in `src/features/finish/copy.ts`, English-only, with its Spanish "owed from ISS"; 0436
  // retired that posture on 2026-10-02.
  'finish.intro':
    "In the next couple of minutes, you're going to receive a welcome email. This email will invite you to log in to our Talent Portal.",
  // The dash is the frame's [12860:29211]: the label runs INTO the numbered list rather than titling it.
  'finish.taskGroupsLabel': 'There are three groups of tasks you will complete —',
  // NUMBERED, not bulleted [0128]; the second runs on into the third by design.
  'finish.taskGroup1':
    'Your profile: this is your full application to work with Integrity Staffing.',
  'finish.taskGroup2': 'Our first two onboarding documents, then when these are complete,',
  'finish.taskGroup3': 'The next group of onboarding documents.',
  // The `Tone=Brand` callout. It ENDS here — the "Be patient … talk with a recruiter" sentence that used to
  // follow is struck [0128] and must never come back.
  'finish.cadence':
    'As you work through each step, it can take roughly 5-10 minutes for the next group of tasks to appear in your profile for you to complete.',
  // ⭐ RECORDED_COPY — the ONE string on this screen Rootstrap wrote [decision 0360, under 0350]. Verbatim
  // from 0360, EN and ES alike, and PROVISIONAL: ISS's own wording replaces it, in this key, when it arrives.
  // Named in `src/features/finish/copy.ts` → `RECORDED_COPY` so the replacement is findable as ours.
  'finish.personalDevice':
    'The next step happens on your own phone — not on this kiosk. Take a seat and watch for your welcome email.',
  // Two keys went with Proposal B [0128]. `finish.waitForRepresentative` was its whole substance, and its
  // message survives in ISS's own words on the closeout (`allDone.body`) rather than in the Rootstrap
  // paraphrase it carried, still owed an ISS sign-off (OQ-58). `finish.done` labelled the action that used
  // to end the flow here; the action is `finish.iUnderstand` now, and the closeout's is `allDone.done`.
  // `10a`'s action is an acknowledgement, not a farewell — the flow continues to the closeout [0128].
  'finish.iUnderstand': 'I understand',
  // KAP-2a · the no-submission branch's hand-off, the Operator's own words [decision 0504 point 1]. Keys of
  // this screen's own: `routing.assistance.*` are still read by `AssociateStatusScreen`'s no-card state, which
  // keeps its old copy until KAS-1a ships the raise that makes this promise true there [0504 point 5], and
  // held by `RoutingScreen`'s `COPY` table.
  'finish.handOff.heading': 'Please have a seat',
  'finish.handOff.body': 'A recruiter will be with you shortly.',
  // `11 · That's it for now` [12302:30407 / 30408]. Heidi Carrel's own words from the 2026-09-01 call:
  // "that's all for now. Please have a seat. Somebody will call you shortly."
  'allDone.heading': "That's it for now",
  'allDone.body':
    'Please have a seat. A representative will call you by name shortly to complete the process.',
  'allDone.done': 'Done',

  /*
   * ── 13 · Drug test info · Figma `13 · Drug test info — Desktop` / `— iPad` / `— Mobile` ──
   *
   * **All three strings are the frame's own text, verbatim** [0040 reserves this copy to ISS], read live
   * from the file on 2026-09-11 and identical at the three widths. The prototype carries the same three
   * sentences word for word (`data-name="drug-test"`), which is the rare case where the two sources agree
   * exactly.
   *
   * ⛔ **There is no fourth string, and the absence is deliberate.** The frame's footer holds a `Divider`
   * and an EMPTY `Actions` row at all three widths — no button, no label — and the prototype states why in
   * as many words: *"NO FOOTER (Tamara, 2026-09-03). This screen tells the candidate to sit down, so it is
   * a hand-off, not a step — and a hand-off screen cannot offer a way onward or back."* So nothing is
   * written here for a control the design removed on purpose.
   *
   * ⚠️ **The screen now carries a Back control anyway — the Operator overrode that ruling on 2026-09-11**
   * (*«dejale el boton back que veo que no esta incluido alli»*; the reasoning is at the control in
   * `DrugTestScreen`). It still adds no string here: the label is `checkIn.back`, the one every Back on
   * the kiosk uses, and 0040 reserves candidate-facing copy to ISS. The footer is still not built.
   */
  'drugTest.heading': 'Please have a seat',
  'drugTest.body': 'A representative will call you by name shortly for your drug test.',
  'drugTest.callout':
    'It’s a simple saliva test. Don’t eat anything or chew gum before it — you can drink water if you would like.',

  /*
   * KPC-1 · `11 · Why a pay card` — Desktop 12389:15576 · iPad 12389:15677 · Mobile 12389:15778, resolved
   * by NAME on the kiosk page and read live 2026-09-14.
   *
   * ⛔ **ALL EIGHT STRINGS ARE UNCONFIRMED, AND THE FRAME IS NOT THE REASON THEY ARE HERE — IT IS ONLY THE
   * BEST EVIDENCE THERE IS.** `04-eng-engineering-context/design-system.md` § *Figma is not the oracle for
   * these* is explicit: copy is governed by 0026 and 0040, **frame text is illustrative, and where they
   * differ the decision wins**. 0155's *"Figma governs the design"* covers layout, spacing and structure —
   * not wording. So the English below is transcribed from the frame so the screen is not blank, and it is
   * **owed a confirmation from ISS under 0040**. Listed string by string in the pull request.
   *
   * ✅ **`payCardWhy.step2` says "the camera", where the frame still draws "the scanner"** — decision 0425
   * (ISS-151, the Operator 2026-09-29): there is no scanner hardware, the read is the device's own camera
   * [0187], and a decision governs the word over the frame [0297]. The sentence is the frame's with that one
   * word replaced, as 0425 quotes it. ISS's sign-off of the pay-card wording stays open → OQ-95.
   *
   * ✅ **`payCardWhy.notYet` carries 0164's SECOND copy requirement**, which the note at the bottom of this
   * block records as owed and absent — *"don't register the card until your first payday"*. The frame now
   * draws it, on THIS screen rather than on the confirmation screen 0164 named. Built where it is drawn;
   * whether the confirmation screen still owes its own is ISS's call, not this file's.
   */
  'payCardWhy.heading': 'How you’ll get paid',
  'payCardWhy.intro':
    'Electronic payments are the most reliable way to make sure you are paid promptly. Everyone who is set to begin an assignment with Integrity receives an ADP pay card, whether or not you plan to sign up for direct deposit. That way, if there is ever an issue with your personal account, we can send your pay to your ADP card instead. Let’s get it set up now, so it’s ready to go.',
  'payCardWhy.step1': 'Please take a card packet from the box next to this kiosk.',
  'payCardWhy.step2':
    'Hold the barcode on the outside of the packet up to the camera — you don’t need to open it.',
  'payCardWhy.step3': 'Check the number we read matches the one on the packet.',
  'payCardWhy.keepCard':
    'Keep the card — it’s yours. We only ever store a safe proxy ID, never the card number itself.',
  'payCardWhy.notYet':
    'Don’t register or activate the card yet — wait until your first payday. Linking it here is all you need to do today.',
  'payCardWhy.continue': 'Continue',

  /*
   * KPC-1 · `13 · Pay card linked` — Desktop 12389:15932 · iPad 12389:16019 · Mobile 12389:16106 — and
   * `R3 · Pay card linked` — Desktop 12389:17070 · iPad 12389:17282 · Mobile 12389:17492. All six resolved
   * by NAME on the kiosk page and read live 2026-09-14; the copy is identical across all six.
   *
   * ⛔ **ALL FOUR STRINGS ARE UNCONFIRMED, AND THE FRAME IS NOT THE REASON THEY ARE HERE — IT IS ONLY THE
   * BEST EVIDENCE THERE IS.** `04-eng-engineering-context/design-system.md` § *Figma is not the oracle for
   * these*: copy is governed by 0026 and 0040, **frame text is illustrative, and where they differ the
   * decision wins**. 0155's *"Figma governs the design"* covers layout, spacing and structure — not
   * wording. The English below is transcribed from the frame so the screen is not blank, and it is **owed a
   * confirmation from ISS under 0040**. Listed string by string in the pull request.
   *
   * ✅ **`payCardLinked.activate` is decision 0164's instruction, on the screen that draws it.** Rhonda
   * Foley reported that a card registered before the first payday cannot be loaded and the candidate gets
   * an error; Heidi ruled the instruction. The Ground Truth recorded it as owed and absent because it
   * believed no such screen existed — it does, at six viewports, and this is where the sentence lives. The
   * prototype carries the same correction in a comment beside its own copy: the string it replaced —
   * *"You can activate the card … on your own phone"* — invited the candidate to register it today, which
   * is the opposite of the ruling. `payCardWhy.notYet` states the other half on the screen before.
   *
   * ✅ **Its verb is *activate*, never *register*** — decision 0424 (the Operator, 2026-09-29; confirmed
   * 2026-10-05): ISS's own verb for the payday act, and what `13 · Pay card linked` now draws. One key serves
   * `13` and `R3`.
   */
  'payCardLinked.heading': 'Your pay card is linked',
  'payCardLinked.body':
    'It’s on your profile now, so your pay will go to this card. Keep it somewhere safe — you won’t be given another one.',
  'payCardLinked.activate':
    'On your first payday you can activate the card and set a PIN from your Talent Portal, on your own phone. Nothing to do before then.',
  'payCardLinked.continue': 'Continue',

  // KPC-1a · `Returning user / 07 · R1 · Pay card — link a new one? (ask)`. English is the frame's own
  // text, verbatim, at all three widths. The heading, subtitle and *Request assistance* are the scan
  // screen's keys, because the frame repeats them word for word — ⚠️ except the iPad frame's subtitle,
  // which alone carries an older sentence; reported to design, not built.
  'payCardAsk.question': 'Do you want to link a new pay card?',
  'payCardAsk.helper': 'If you have a card with you, we can link it now.',
  'payCardAsk.linkNew': 'Link a new pay card',

  // E7 · `Edge cases / E7 · Pay card — do you need a new one? (returning) — Desktop`, the menu's pay-card row
  // with a card on file [Operator, 2026-10-16]. English is the frame's own text, verbatim — the eyebrow
  // included, which the frame sets in capitals.
  'payCardNeed.eyebrow': 'PAY CARD',
  'payCardNeed.question': 'Do you need a new pay card?',
  'payCardNeed.helper': "If you already have one, you're all set — there's nothing to do here.",
  'payCardNeed.yes': 'Yes, I need a new one',

  // KPC-1 · Figma 12021:41960. English is the frame's own text, verbatim.
  'payCard.needHelp': 'Request assistance',
  'payCard.confirmHint':
    "Type it again — copy/paste is off so a typo can't slip through (a mistype means you wouldn't get paid).",
  'payCard.orTypeItIn': 'OR TYPE IT IN',
  // The frame's own words [12275:27135, read live 2026-09-08]. ⚠️ ISS dropped the sentence that stated the
  // OBLIGATION — "every new associate needs a pay card. Take one from the stack" — so 0089's rule and the
  // instruction to pick a card up are no longer on the screen that needs them. Reported, not restored:
  // 0040 reserves this copy to ISS.
  'payCard.intro':
    'Scan the barcode, or type the proxy ID twice. That’s all we keep — never the card number.',
  // KPC-1d's `payCard.securityFraming` — what the camera does, said before the candidate points it. No frame
  // draws it; the words are decision 0490's (approved, the Operator, 2026-10-14), in both languages. Whether
  // it must also say the card holds no sensitive data until activated is 0493's question — the Operator
  // answered B on 2026-10-16 (the line is enough; 0493 in review). Do not add words to it here [0040].
  'payCard.securityFraming':
    'We only read the barcode on the packet. Nothing is recorded or saved from the camera.',
  // Between the camera and "OR TYPE IT IN" [12441:27925]. It is the only place the screen says WHERE the
  // barcode is, which is the question a candidate holding an unfamiliar card actually has.
  // ✅ The sentence decision 0425 point 3 rules for `12 · Scan` and `R2` (the Operator, 2026-10-05): the
  // frame's "Hold the barcode on the outside of the packet flat against the scanner." with "up to the
  // camera" — there is no scanner hardware [0187], and a decision governs the frame's word [0297]. Still
  // the barcode on the outside of the sealed packet, never the back of the card [0164]. Replace this
  // string, never add alongside it. ISS's sign-off of the pay-card wording stays open → OQ-95.
  'payCard.scanHint': 'Hold the barcode on the outside of the packet up to the camera.',
  'payCard.scanTitle': 'Scan your pay card',
  'payCard.idLabel': 'Proxy ID',
  'payCard.idConfirmLabel': 'Confirm proxy ID',
  'payCard.save': 'Save & continue',
  'payCard.mismatch': "The two entries don't match. Please check the ID and type it again.",
  /*
   * ⚠️ **The message names the LENGTH and never the prefix** [decision 0181; `contracts.md` § B]. The api
   * withholds the prefix from its own error for a reason — the endpoint is unguarded and the value writes
   * into ISS's payroll system of record — and copy on a screen in a public lobby is a worse place to
   * publish it than a 400 body. "16 digits" is the whole of what this app checks and the whole of what it
   * may say.
   */
  'payCard.badFormat': 'The pay card ID is 16 digits. Please check the card and enter all 16.',
  'payCard.rejected': "That ID doesn't look right. Please check the card and try again.",
  // KPC-1c's failed read — a barcode decoded that is not a proxy id, so it is never offered for
  // confirmation. The frame's own two lines, verbatim [`New user / 10 · Scan — failed read (state)`, error
  // callout, read live 2026-10-16]. "Request assistance" is `payCard.needHelp`, the footer button it names.
  'payCard.failedRead.title': 'We couldn’t read the card.',
  'payCard.failedRead.body':
    'Try the scan again — hold the barcode flat, without covering it with your fingers. If it still won’t read, use Request assistance below.',
  'payCard.scan.title': 'Scan your pay card',
  'payCard.scan.starting': 'Starting the camera…',
  // ⚠️ NOT the `12 · Scan` sentence. That is `payCard.scanHint` above, decided by 0425 point 3. THIS key is
  // a different surface: the prompt inside the scanning overlay (`ScanOverlay.tsx`, the `scanning` state),
  // which no frame draws. The Ground Truth's string contract (KPC-1d) uses the name
  // `payCard.scan.instruction` for the scan screen's sentence; that is an illustrative key name and it
  // means `payCard.scanHint`, not this key. This string is still INTERIM, the app's own wording, owed to
  // ISS → OQ-95. Replace it; never add alongside it. It once read "the back of the card", which
  // contradicts 0164 (the barcode is read through the sealed package, never opened); the camera half
  // was already right [0187].
  'payCard.scan.instruction':
    'Hold the sealed package up to the camera with the barcode inside the dashed frame. Keep it flat and still.',
  'payCard.scan.found': 'Card read. Check the number below before you continue.',
  'payCard.scan.denied':
    'The camera was not allowed. You can enter the ID by hand, or a representative can do it for you.',
  'payCard.scan.unsupported':
    'This device cannot read a barcode on its own. Enter the ID by hand, or a representative can do it for you.',
  'payCard.scan.unavailable':
    'The camera did not open. Enter the ID by hand, or a representative can do it for you.',
  'payCard.scan.videoLabel': 'Camera view for scanning your pay card',
  'payCard.scan.use': 'Use this card',
  'payCard.scan.again': 'Scan again',
  'payCard.scan.typeInstead': 'Enter the ID by hand',
  'payCard.scan.recruiter': 'Ask a representative',
  // ⛔ OWED AND ABSENT — 0164's SECOND copy requirement, deliberately not written here. Rhonda Foley
  // [20:48]: a card registered before the first payday cannot be loaded, and the candidate gets an error.
  // Heidi Carrel ruled the fix [21:17]: *"hold on to this card, you'll register it on your first payday."*
  // 0164 places it on the CONFIRMATION screen at the end of the pay-card flow — *"the last portion of
  // this"*, which Tamara Insua confirmed — and this build HAS no such screen: both success paths in
  // `PayCardScreen` exit to the arrival menu or to `finish`. So it needs a surface that does not exist,
  // and wording 0164 says lives in Heidi's Figma annotations rather than in the transcript. Left as a
  // marker rather than invented: 0040 reserves candidate-facing copy to ISS. → OQ-95.

  /*
   * ───────────────────────────────────────────────────────────────────────────────────────────────────
   * `help.overlay.*` — the *Need help?* overlay [`NeedHelpDialog`], its three drawn states [0441].
   *
   * Every string is the frames' own, read from `10 · Overlay · Help — summon a rep (launch)`, `— who's
   * asking?` and `— in the recruiter queue` [0441 § *The three states*; 0297], and replaces the
   * chat-approved `help.dialog.*` set of 2026-09-15, which 0441 supersedes. The header's «AI» and «Support ·
   * always available» render as drawn by the Operator's ruling of 2026-10-07 [0441 point 6] — display only.
   *
   * ⛔ **The queue state's body has NO key, on purpose** [0457 points 1–3]: the drawn body is not built, ISS
   * owes the string (OQ-152), and until it is recorded the body renders nothing. Do not add a placeholder.
   * ⛔ Neither has the box's *Reason* row, label or value, though the iPad and Mobile frames draw it: the
   * reason is the recruiter's alone and the candidate never sees it [0482, amending 0441 point 5; 0045].
   * ───────────────────────────────────────────────────────────────────────────────────────────────────
   */
  'help.overlay.avatar': 'AI',
  'help.overlay.support': 'Support · always available',
  'help.overlay.close': 'Close',
  'help.overlay.launch.heading': 'Need help?',
  'help.overlay.launch.body':
    "We'll let a recruiter know you're here. You can keep working while you wait.",
  'help.overlay.launch.submit': 'Talk to a recruiter',
  'help.overlay.name.heading': "What's your name?",
  'help.overlay.name.noRecord':
    "We don't have a record for you yet, so a recruiter won't know who to look for. Tell us your name and we'll add you to the queue.",
  'help.overlay.name.label': 'Name',
  'help.overlay.name.placeholder': 'e.g. Maria A.',
  'help.overlay.name.hint': 'First name and last initial is enough.',
  'help.overlay.name.error': 'Enter a name so the recruiter knows who to look for.',
  'help.overlay.name.submit': 'Add me to the queue',
  'help.overlay.queue.heading': "You're in the recruiter queue",
  'help.overlay.queue.boxHeading': 'WHAT THE RECRUITER SEES',
  'help.overlay.queue.name': 'Name',
  'help.overlay.queue.site': 'Site',
  /** Exactly `Anonymous` — the recruiter's own word for a nameless row [0455 § *The string*; 0441 point 5]. */
  'help.overlay.queue.anonymous': 'Anonymous',
  'help.overlay.queue.footnote':
    "Not a generic alert — whoever calls already has this, so you won't repeat yourself.",
  'help.overlay.stillOpen': 'Your application is still open — closing this returns you right back.',
} as const;

export type TranslationKey = keyof typeof en;
export type EnDictionary = Record<TranslationKey, string>;

/**
 * The Spanish column is written in **neutral Latin-American Spanish, second person «tú»** — no voseo, no
 * «usted», no regionalism.
 *
 * Operator ruling, 2026-09-15: *«Usemos español neutro siempre.»* It replaces the voseo («podés»,
 * «revisá», «para vos») this column used to mix into «tú», and it supersedes the prototype's own
 * `data-es`, which is written in «usted» — so one candidate is addressed one way on every screen, and the
 * prototype is not the register's source even where it carries Spanish.
 *
 * The ruling is the app's to make: **the Figma file draws no Spanish frame at any width**, so 0155 (Figma
 * governs copy) does not reach this register. 0040 still reserves the candidate-facing WORDING to ISS, and
 * nothing below re-authors a sentence — only its grammatical person moved, the legal consent block
 * included.
 *
 * `dictionary.test.ts` fails on a voseo form in any Spanish value, so the rule holds against the next
 * string somebody adds rather than living in this comment.
 */
const es: EnDictionary = {
  'fit.heading': 'Solo unas verificaciones rápidas',
  'fit.subtitle':
    "Sobre el día a día de {job}. Un 'no' no te descarta — solo significa que un reclutador te va a contactar.",
  'fit.continue': 'Continuar',
  'fit.loading': 'Preparando tus preguntas…',
  'fit.unavailable.heading': 'Por favor busca a un representante',
  'fit.unavailable.body':
    'No pudimos cargar las preguntas en este momento. Un miembro del personal puede ayudarte desde acá.',
  'fit.unavailable.done': 'Listo',
  // The English, as stored — not a translation left undone [0367 note 4; 0366]. See the `en` entry.
  'fit.eeoNote':
    'Note - Integrity complies with all applicable laws ensuring equal employment opportunities for individuals with disabilities and will make reasonable accommodation for a qualified individual with a disability, unless it results in undue hardship and/or a health or safety concern.',
  // The app's own Spanish for ISS's new heading [ISS-152], owed ISS's review [0328 rule 1].
  'submitted.heading': 'Tu interés en el puesto fue enviado.',
  'submitted.body':
    'Te postulaste al puesto de {job}. Un reclutador lo revisa a continuación, y te confirmamos por correo cuando tu asignación esté lista.',
  'submitted.oneMore': 'Antes de irte, queda una cosa más por configurar acá en el kiosco.',
  'submitted.next': 'Siguiente',
  'profileUpdate.heading': 'Bienvenido de nuevo — actualicemos tu perfil',
  'profileUpdate.subtitle':
    'Ya completamos lo que tenemos en tu legajo — revisa y edita lo que haya cambiado.',
  // The Figma file draws no Spanish frame at any width, so this is the app's own and is **owed an ISS
  // confirmation under 0040** [precedent: `payCardWhy.*`, `payCardLinked.*`, `i9.assistQuestion`]. It is
  // the word this column already uses for the same field — `application.phone` reads «Teléfono» and
  // `profileUpdate.emergencyPhone` reads «Teléfono de emergencia» — not a new coinage. A noun, so it
  // conjugates nothing and carries no register of its own.
  'profileUpdate.phone': 'Teléfono',
  'profileUpdate.preferences': 'Preferencias',
  'profileUpdate.shiftPreference': 'Preferencia de turno',
  'profileUpdate.anyShift': 'Cualquier turno',
  'profileUpdate.dateAvailable': 'Fecha disponible',
  'profileUpdate.contact': 'Contacto y emergencia',
  'profileUpdate.homeAddress': 'Domicilio',
  'profileUpdate.emergencyContact': 'Contacto de emergencia',
  'profileUpdate.emergencyPhone': 'Teléfono de emergencia',
  'profileUpdate.sensitive':
    'El depósito directo, la tarjeta de pago y el W-4 se actualizan en privado desde tu teléfono — nunca en este kiosco compartido.',
  'profileUpdate.continue': 'Todo bien, continuar',
  'profileUpdate.saveRejected':
    'No pudimos guardar tus cambios — revisa los campos marcados e intenta de nuevo.',
  'profileUpdate.fieldRejected': 'Revisa este campo.',
  'profileUpdate.phone.error': 'Ingresa un teléfono de 10 dígitos, solo números — o déjalo vacío.',
  'profileUpdate.payRate.error': 'Ingresa un pago por hora, por ejemplo 18.50 — o déjalo vacío.',
  'profileUpdate.dateAvailable.error': 'Elige hoy o una fecha posterior — o déjalo vacío.',
  'application.heading': 'Crea tu perfil',
  'application.subtitle': 'Solo lo básico para encontrarte un puesto — toma unos 2 minutos.',
  'application.personal': 'Información personal',
  'application.firstName': 'Nombre',
  'application.firstName.error': 'El nombre es obligatorio.',
  'application.lastName': 'Apellido',
  'application.lastName.hint': 'Lo trajimos de tu búsqueda — edítalo si hace falta.',
  'application.lastName.error': 'El apellido es obligatorio.',
  'application.email': 'Correo electrónico',
  'application.email.error': 'Ingresa una dirección de correo válida.',
  'application.phone': 'Teléfono',
  'application.phone.error': 'Ingresa un teléfono de 10 dígitos — solo números.',
  'application.location': 'Ubicación',
  'application.city': 'Ciudad',
  'application.city.error': 'La ciudad es obligatoria.',
  'application.state': 'Estado',
  'application.state.error': 'Elige un estado.',
  'application.zip': 'Código postal',
  'application.zip.error': 'Ingresa un código postal de 5 dígitos.',
  'application.employment': 'Detalles de empleo',
  'application.payRate': 'Pago por hora deseado',
  'application.payRate.hint': 'Ingresa la tarifa por hora que buscas.',
  'application.payRate.error': 'Ingresa un pago válido.',
  'application.startDate': '¿Cuándo puedes empezar?',
  'calendar.open': 'Abrir el calendario',
  'calendar.close': 'Cerrar el calendario',
  'calendar.previousMonth': 'Mes anterior',
  'calendar.nextMonth': 'Mes siguiente',
  'application.startDate.error': 'Ingresa la fecha en que puedes empezar.',
  'application.workAuthorized': '¿Estás legalmente autorizado para trabajar en los EE. UU.?',
  'application.isAdult': '¿Tienes 18 años o más?',
  'application.yes': 'Sí',
  'application.no': 'No',
  'application.answerRequired': 'Responde esta pregunta.',
  'application.sensitive':
    'Los datos sensibles, como tu número de seguro social y tu cuenta bancaria, se completan en privado desde tu propio teléfono — nunca en este kiosco compartido.',
  'application.consentHeading': 'Consentimiento de contacto',
  'consent.review': 'Revisa la siguiente información antes de continuar.',
  /*
   * ⚠️ **Only the opening clause changed**, and it had to: the previous Spanish said *"Al marcar esta
   * casilla"* — "by checking this box" — describing a checkbox the consent no longer has [0160]. The rest is
   * the authored Spanish that was already here, untouched. ISS owns this copy [0040] and owes the Spanish
   * for the whole block; the clause is corrected rather than left saying something false in front of a
   * Spanish-speaking candidate.
   */
  'application.consentFull':
    'Al seleccionar «Acepto», otorgas a Integrity Staffing Solutions, sus afiliadas y sus socios contratados permiso para contactarte sobre ofertas de empleo mediante llamadas, llamadas generadas por inteligencia artificial, mensajes de texto o correos. Al enviar la postulación, asegúrate de que el teléfono que indicaste sea uno que te esté permitido usar y en el que estés autorizado a recibir llamadas, llamadas generadas por inteligencia artificial, mensajes de texto o correos. Si tu número cambia durante el proceso, avísale a Integrity cuanto antes. Puedes dejar de recibir estas comunicaciones en cualquier momento diciendo o escribiendo STOP. Para ayuda, escribe HELP. La frecuencia de los mensajes varía. Pueden aplicarse cargos por mensajes y datos. Las operadoras no son responsables por mensajes demorados o no entregados. Puedes consultar nuestra política de privacidad en https://integritystaffing.com/privacy-policy-guidelines. * Esta postulación incluye un Acuerdo de Arbitraje vinculante que exige que tú e Integrity Staffing resuelvan por arbitraje los reclamos cubiertos, incluidos, sin limitación, los que surjan de tu postulación y de tu empleo (si te contratan), que serán decididos por un único árbitro mediante arbitraje final y vinculante, y no por un tribunal ni por un juicio con jurado. *',
  'consent.question': '¿Aceptas que te contactemos?',
  'consent.agree': 'Acepto',
  'consent.decline': 'No acepto',
  /*
   * ⚠️ **Spanish authored here, in the register of the entries above; the frames supply English only.**
   * [0040] reserves candidate-facing copy to ISS, so these thirteen strings are the ones to put in front of
   * them for confirmation — they are not machine-translated (§ 7 forbids that) and they are not legal text
   * being drafted: each is the Spanish of an English sentence ISS has already written and approved.
   */
  'blocked.consent.heading': 'No podemos continuar sin tu consentimiento',
  'blocked.consent.body':
    'Necesitamos tu permiso para contactarte sobre trabajos — sin él no tenemos forma de contarte sobre puestos disponibles ni sobre los próximos pasos.',
  'blocked.consent.whatToDo':
    'Si cambias de opinión, vuelve atrás y elige «Acepto». No se perdió nada de lo que escribiste.',
  'blocked.age.heading': 'Necesitas tener 18 años o más',
  'blocked.age.body':
    'Los puestos que cubrimos desde este kiosco requieren que tengas al menos 18 años.',
  'blocked.age.whatToDo':
    'Vuelve a vernos cuando cumplas 18 — nos va a dar gusto ayudarte entonces.',
  'blocked.auth.heading': 'No podemos continuar sin autorización para trabajar',
  'blocked.auth.body':
    'Para ubicarte en un puesto necesitamos que estés autorizado a trabajar en los Estados Unidos.',
  'blocked.auth.whatToDo': 'Si tu situación cambia, puedes postularte de nuevo.',
  'blocked.goBack': 'Volver y cambiar mi respuesta',
  'blocked.exit': 'Salir',
  'application.continue': 'Continuar',
  'application.saveRejected':
    'No pudimos guardar tus datos — revisa los campos marcados e intenta de nuevo.',
  'welcome.title': 'Bienvenido a Integrity',
  // The em dash the English took from the frame, mirrored. ⚠️ **There is no Spanish frame in the file** —
  // 0026 obliges both languages and 0040 reserves the copy to ISS, so this half is the app's own and is
  // **owed ISS confirmation under 0040**. The register is left exactly as it was («tú», as the rest of this
  // screen); only the punctuation moved, so nothing here re-authors a validated sentence.
  'welcome.subtitle':
    'Postúlate a trabajos de almacén y logística cerca de ti — en solo unos minutos.',
  'welcome.languageLabel': 'Idioma',
  'welcome.tapToStart': 'Toca para empezar',
  'applied.heading': '¿Alguna vez te postulaste con nosotros?',
  'applied.subtitle':
    'Aunque nunca hayas trabajado un turno — con haberte postulado ya tenemos tu registro.',
  // Spanish for the pair above, and it ships in the SAME change [0026]: an EN-only edit would leave the
  // two languages promising different things. There are no Spanish frames in the file at all, so the
  // source cannot be Figma — these are the prototype's own ES renderings of these two sentences, taken
  // verbatim. Nothing here is authored, and the wording is owed ISS's validation [0040].
  'welcome.newApplicant': 'Nunca me he postulado en Integrity.',
  'welcome.returning': 'Ya me postulé o trabajé en Integrity.',
  'checkIn.heading.new': '¡Bienvenido! Vamos a empezar',
  'checkIn.heading.returning': '¡Bienvenido de nuevo!',
  'checkIn.email': 'Correo electrónico',
  'checkIn.lastName': 'Apellido',
  'checkIn.birthDay': 'Día de nacimiento',
  'checkIn.birthMonth': 'Mes de nacimiento',
  'checkIn.ssnLast4': 'Últimos 4 del SSN',
  'help.fab': '¿Necesitas ayuda?',
  'badge.kiosk': 'Kiosco',
  'exit.control': 'Salir',
  /*
   * Follows the English onto decision 0340's strings, in the same change [0026]: leaving the old Spanish
   * would have kept the promise of erasure (*«Se borrará…»*) in front of exactly the candidates 0340 was
   * written to stop misleading.
   *
   * ⚠️ **ISS supplied no Spanish, and there is no Spanish frame.** 0340 puts these on the rule every other
   * candidate-facing string follows [0328 rule 1: the kiosk's own chrome is translated here and ISS reviews
   * it], so they are the app's own, in the neutral «tú» this column uses, and **owed ISS confirmation under
   * 0040** — listed string by string in the pull request [precedent: `payCardWhy.*`, `payCardLinked.*`].
   * «Estás seguro» is the form `jobs.help.title` already uses; «sistema de seguimiento de candidatos» is the
   * usual Spanish name for an applicant tracking system. Capitals and the missing full stop follow ISS's
   * English.
   */
  'exit.heading': '¿Estás seguro de que quieres salir ahora?',
  'exit.body':
    'La información enviada hasta ahora se guardará en nuestro sistema de seguimiento de candidatos',
  'exit.keepGoing': 'NO',
  'exit.confirm': 'SÍ',
  'checkIn.subheading':
    'Te pediremos algunos datos para comenzar. Si ya tenemos un registro tuyo, continuaremos donde lo dejaste.',
  'checkIn.emailPlaceholder': 'nombre@ejemplo.com',
  // Follows the English into the frame's order. No Spanish frame exists, so this is the app's own and is
  // **owed ISS confirmation under 0040**; what is collected is unchanged [decision 0032].
  'checkIn.dob': 'Fecha de nacimiento (mes y día)',
  'checkIn.dobBothRequired': 'Elige el día y el mes.',
  // The prototype's `data-es` for the same three slots, in the column's «tú» [Operator, 2026-09-15] — the
  // email and last-name lines are the ones `application.*.error` already use. Owed ISS confirmation (0040).
  'checkIn.emailInvalid': 'Ingresa una dirección de correo válida.',
  'checkIn.lastNameRequired': 'El apellido es obligatorio.',
  'checkIn.ssnLast4Invalid': 'Ingresa los últimos 4 dígitos de tu SSN.',
  'checkIn.dobDay': 'Día',
  'checkIn.dobMonth': 'Mes',
  'routing.checking': 'Verificando tu registro…',
  'checkIn.back': 'Atrás',
  'lookup.submit': 'Continuar',
  'checkIn.allFourRequired':
    'Usamos este conjunto completo de datos en conjunto para confirmar tu identidad — por favor completa los cuatro.',
  'lookup.pending': 'Verificando tus datos…',

  'routing.new.heading': 'Empecemos tu solicitud',
  'routing.new.body': 'No encontramos un registro existente, así que crearemos uno ahora.',
  'routing.returning.heading': 'Bienvenido de nuevo',
  'routing.returning.body': 'Encontramos tu registro. Continuemos donde lo dejaste.',
  'routing.resume.heading': 'Bienvenido de nuevo',
  'routing.resume.body':
    'Ya habías comenzado con nosotros. Continuaremos esa solicitud en lugar de iniciar una nueva.',
  'routing.activePlacement.heading': 'Actualmente tienes una asignación',
  'routing.activePlacement.body': 'Los detalles de tu asignación están en tu portal.',
  'routing.continue': 'Continuar',

  /*
   * Follows the English onto the frame, in the same change.
   *
   * ⚠️ **There is no Spanish frame anywhere in the Figma file**, so the badge and the heading are the app's
   * own, in the neutral «tú» this dictionary uses throughout, and **owed ISS confirmation under 0040** — the
   * precedent `payCardWhy.*` and `payCardLinked.*` set, not a second convention. Both come from wording
   * already in this column rather than newly authored: *«Toma asiento, por favor»* is `drugTest.heading`'s
   * exact Spanish, because the English of the two is now the same sentence and one string may not be two.
   *
   * ⭐ The body is not the app's: it is the Operator's own Spanish [decision 0504 point 1; 03-us-kid-7a].
   */
  'seeARep.badge': 'Rep',
  'seeARep.heading': 'Toma asiento, por favor',
  'seeARep.body': 'Un reclutador te atenderá en breve.',
  'seeARep.reason': 'Estás acá por: {reason}',
  'seeARep.documents':
    'Por favor ten a mano tu documentación de autorización de empleo para revisarla.',
  'routing.assistance.heading': 'Por favor acude a un representante',
  'routing.assistance.body':
    'Un miembro de nuestro equipo en la recepción puede ayudarte a registrarte.',
  'routing.assistance.startOver': 'Empezar de nuevo',

  /*
   * KPW-1c · E4. ⭐ **Machine-translated, for ISS to review** [0328 rule 1; OQ-134]. By 0328's provenance
   * test this is the app's own copy (an error screen's chrome), not application or legal text from a system
   * of record — the same reading 0436 made for screen 10a. There is no Spanish frame. Neutral «tú» [0254].
   * All three were approved by the Operator on 2026-10-08, with `getHelp` changed to «Pedir ayuda» (the
   * same words as `payCard.needHelp`).
   */
  'submissionFailed.heading': 'No pudimos enviar tu solicitud',
  'submissionFailed.body':
    'Algo salió mal de nuestro lado — no fue nada que hayas hecho. Tus datos están guardados. Un representante terminará esto contigo.',
  'submissionFailed.getHelp': 'Pedir ayuda',

  // KID-7 · the dead end's own help button: the same words as `submissionFailed.getHelp`, which the Operator
  // approved on 2026-10-08. No new Spanish copy.
  'deadEnd.getHelp': 'Pedir ayuda',

  'throttle.heading': 'Espera antes de intentar de nuevo',
  'throttle.body':
    'Hemos tenido varios intentos sin éxito con estos datos. Puedes intentar de nuevo en:',
  'throttle.recruiter':
    'Si esto sigue ocurriendo, por favor pide ayuda a un miembro de nuestro equipo.',
  'throttle.tryAgain': 'Intentar de nuevo',
  'throttle.remaining': 'Tiempo restante',

  // The prototype's `#emailDialog` `data-es`, moved from «usted» to the column's neutral «tú» [Operator,
  // 2026-09-15]. There is no Spanish frame; the wording is owed ISS confirmation under 0040, like the rest.
  'differentEmail.heading': 'Encontramos tu cuenta',
  'differentEmail.body':
    'Tu cuenta está registrada con {email}. El correo que acabas de ingresar es distinto — corrígelo abajo si fue un error, o continúa para conservar el nuevo.',
  'differentEmail.emailLabel': 'Tu correo',
  'differentEmail.continue': 'Continuar',
  'differentEmail.useOnFile': 'Usar el correo de mi cuenta',
  'error.generic': 'Algo salió mal. Por favor pide ayuda a un miembro del personal.',

  'arrival.heading': '¿Qué te trae hoy?',
  'arrival.loading': 'Preparando tus opciones…',
  'jobs.heading': 'Puestos abiertos cerca de ti',
  'jobs.subheading': 'Todos los puestos para los que esta oficina está contratando ahora.',
  'jobs.featured': 'Destacado',
  'jobs.untitled': 'Puesto abierto',
  'jobs.loading': 'Buscando puestos cerca de ti…',
  'jobs.help.title': '¿No estás seguro o solo tienes una pregunta?',
  'jobs.help.body':
    'Hay ayuda disponible todo el tiempo que estés aquí: desde un chat rápido con IA hasta una persona real si la necesitas.',
  'jobs.help.action': 'Obtener ayuda →',

  'jobs.empty.heading': 'Por ahora no hay una coincidencia exacta',
  'jobs.empty.body':
    'Hoy no tenemos un puesto que encaje contigo en esta ubicación. Así seguimos avanzando:',
  'jobs.empty.recruiter.title': 'Un reclutador ya viene',
  'jobs.empty.recruiter.body':
    'Un reclutador puede buscar más ampliamente y revisar tus opciones contigo.',
  'jobs.empty.unreachable.title': 'Por favor busca a un representante',
  'jobs.empty.unreachable.body':
    'No pudimos cargar los puestos en este momento. Un miembro del personal puede ayudarte desde acá.',
  'jobs.empty.done': 'Listo',

  'jobs.detail.heading': 'Detalle del puesto',
  'jobs.detail.shift': 'Turno',
  'jobs.detail.pay': 'Pago',
  'jobs.detail.jobType': 'Tipo de puesto',
  'jobs.detail.location': 'Ubicación',
  'jobs.detail.jobId': 'Número de puesto',
  'jobs.detail.fullDescription': 'Descripción completa del puesto',
  'i9.heading': 'Completa tu I-9',
  'i9.body': 'Tu I-9 es el formulario federal de autorización de trabajo y tiene dos partes.',
  'i9.section1':
    'La Sección 1 es tuya. Complétala en tu Talent Portal, desde tu teléfono o tu computadora.',
  'i9.section2':
    'La Sección 2 la tiene que completar en persona un Representante Autorizado — no puedes hacerla tú.',
  'i9.staffCanHelp':
    'Si hoy tienes contigo tus documentos de autorización de trabajo, alguien de nuestro equipo puede completar la Sección 2 contigo ahora.',
  // Neutral «tú», like the rest of this dictionary. There is no Spanish frame — 0040 reserves the
  // ENGLISH copy to ISS and 0026 obliges both languages, so the Spanish is the app's own and travels in
  // the same commit.
  'i9.assistQuestion': '¿Quieres que hoy te ayude alguien de nuestro equipo?',
  'i9.helpYes': 'Sí, ayúdame hoy',
  'i9.helpNo': 'No, lo va a hacer otra persona',
  // The English, as stored — not a translation left undone [0359 ruling 2; 0328 rule 2]. ISS owes the
  // Spanish; until it arrives these two are on `SAME_IN_BOTH`. See the `en` entries. The QR itself DOES
  // follow the language: a Spanish kiosk encodes USCIS's Spanish page [0370].
  'i9.documents.sentence':
    'To complete your Form I-9, you must provide documentation to confirm your identity and authorization to work. If you need additional guidance on what documentation can be used for your Form I-9, please click here.',
  'i9.documents.linkLabel': 'please click here',
  // Kiosk chrome, so Spanish is the app's own [0328 rule 1] — hand-authored, neutral Latin-American «tú»
  // [0254], no machine translation. There is no Spanish frame; ISS reviews it with the English [0040].
  'i9.documentsQr.title': 'Documentos aceptables para el Formulario I-9',
  'i9.documentsQr.body':
    'Escanea este código para abrir la lista oficial de USCIS en tu propio teléfono.',
  'i9.documentsQr.caption': 'Escanea con la cámara de tu teléfono',
  'i9.documentsQr.close': 'Cerrar',
  // ⭐ The submission control, following the English onto the frame in the same change — never English
  // updated and Spanish left behind, least of all on the button that writes to Bullhorn. No Spanish frame
  // exists, so this is the app's own and is **owed ISS confirmation under 0040** [precedent: `payCardWhy.*`,
  // `payCardLinked.*`]. Register-neutral on purpose — it conjugates no second person at all, so
  // the ruling on the Spanish register does not reach it.
  'jobs.detail.select': 'Sí, me interesa este puesto.',
  'jobs.detail.selecting': 'Enviando tu postulación…',
  // Decision 0305. There is no Spanish frame, so this is the app's own wording and is **owed ISS
  // confirmation under 0040**. Its source is still OQ-134 [precedent: `jobs.detail.select` above]. Written
  // in the first person, like the English "Let me…" and like the interest button beside it, so it conjugates
  // no second person and the register ruling does not reach it.
  'jobs.detail.lookForAnother': 'Quiero buscar otro puesto',
  // KJM-3a AC-8. These are the kiosk's own copy, not application or legal text, so the Spanish is a machine
  // translation that ISS reviews [0328 rule 1]. It uses neutral Latin-American «tú» [0254]. There is no
  // Spanish frame. *Postulación* and *prueba antidopaje* are the words this dictionary already uses
  // (`jobs.detail.selecting`, `arrival.drugTest.title`).
  'jobs.detail.sendsApplication':
    'Al seleccionar este puesto, envías tu postulación. Después vienen un par de preguntas rápidas.',
  'jobs.detail.contingencies':
    'Algunos clientes requieren una verificación de antecedentes y/o una prueba antidopaje antes de empezar.',
  'associate.heading': 'Bienvenido de nuevo',
  'payCard.heading': 'Vincula tu tarjeta de pago',
  'finish.heading': 'Tus próximos pasos',

  'stepper.label': 'Tu progreso',
  'stepper.findJob': '1 · Buscar trabajo',
  'stepper.questions': '2 · Preguntas',
  'stepper.submitted': '3 · Enviado',
  'stepper.getSetUp': '4 · Prepararte',
  'stepper.allSet': '5 · Listo',
  // ⚠️ These seven are written in NEUTRAL Latin-American Spanish — «tú», no voseo, no regionalisms —
  // under the Operator's standing ruling of 2026-09-15. The rest of this column is still voseo and is
  // being swept on its own branch; do not harmonise these back toward it.
  'stepper.ringProgress': '{current} de {total}',
  'stepper.next': 'Siguiente: {label}',
  'stepper.findJob.bare': 'Buscar trabajo',
  'stepper.questions.bare': 'Preguntas',
  'stepper.submitted.bare': 'Enviado',
  'stepper.getSetUp.bare': 'Prepararte',
  'stepper.allSet.bare': 'Listo',
  // KID-5. ⚠️ Spanish is owed from ISS [decisions 0026, 0040] — no machine translation was used, but these
  // were authored here rather than supplied, and they are replaced when ISS's words arrive.
  // The seven `.action` keys that stood here are gone with the button they labelled — see the English
  // block. `hoy` tracks the `today` restored to the English sub-heading from the frame.
  'arrival.subheading': 'Tu información está al día. Elige a qué viniste hoy.',
  // «Reconfirmar», not «Confirmar» — the prototype's own verb for this row (`hubSeeInfo`), and the one that
  // matches the English "Reconfirm". The rest of this row keeps the dictionary's «tú»; see the note above
  // the English block for what was and was not taken from the prototype.
  'arrival.reconfirmAssignment.title': 'Reconfirmar la información de mi asignación',
  'arrival.reconfirmAssignment.subtitle': 'A dónde vas, tu turno y tu primer día',
  'arrival.seeMyApplications.title': 'Ver a qué me postulé',
  'arrival.seeMyApplications.subtitle': 'Mira en qué estado está tu postulación',
  'arrival.applyForJob.title': 'Buscar un trabajo',
  'arrival.applyForJob.subtitle': 'Ver puestos abiertos cerca de ti',
  'arrival.onboardingPaperwork.title': 'Completar mi documentación de incorporación / I-9',
  'arrival.onboardingPaperwork.subtitle':
    'Podemos ayudarte a completar hoy, en persona, la Sección 2 de tu Formulario I-9.',
  'arrival.drugTest.title': 'Completar mi prueba antidopaje',
  'arrival.drugTest.subtitle':
    'Las pruebas antidoping se harían probablemente en el evento de contratación.',
  'arrival.payCard.title': 'Vincular tarjeta de pago',
  'arrival.payCard.subtitle': 'Configura cómo recibes tu pago',
  'arrival.somethingElse.title': 'Pedir ayuda a un representante',
  'arrival.somethingElse.subtitle': 'Alguien del lugar vendrá a ayudarte',

  'associate.subheading': 'Estos son los datos de tu asignación.',
  'associate.subheading.applied': 'Esto es a lo que te postulaste.',
  'associate.subheading.applications': 'Estos son los {count} trabajos a los que te postulaste.',
  'associate.application': 'Postulación {n}',
  'associate.client': 'Cliente',
  'associate.location': 'Ubicación',
  'associate.shift': 'Turno',
  'associate.nextShift': 'Próximo turno',
  'associate.firstDay': 'Primer día',
  'associate.portalDeflection.title':
    '¿Necesitas actualizar tu pago, tus impuestos o tus datos personales?',
  /*
   * ⚠️ **DELIBERATELY NOT CHANGED with the English above, and this is the reasoning rather than an
   * oversight.** The English edit was orthographic — *talent portal* → *Talent Portal*, the frame's casing
   * — so it promises nothing the Spanish does not; leaving these is not the "English updated, Spanish
   * stale" failure that rule exists for. Making the Spanish match would mean choosing between
   * «portal de talento» here and the untranslated *Talent Portal* that `i9.section1` and
   * `payCardLinked.activate` already use, and that is authoring candidate-facing Spanish with no frame,
   * no decision and nobody asking. **Reported to ISS instead**: the app names one destination two ways in
   * Spanish, and 0040 makes the wording theirs.
   */
  'associate.portalDeflection':
    'Eso se hace en tu portal de talento, al que puedes entrar desde tu teléfono o tu computadora. Si no recuerdas cómo acceder, avísanos y le informamos a un representante de Integrity que necesitas ayuda.',
  'associate.portalDeflection.action': 'No puedo entrar a mi portal de talento →',
  'associate.portalDeflection.done': 'Ya le avisamos a un representante.',
  'associate.done': 'Listo',
  'associate.loading': 'Buscando tu asignación…',

  // Machine-translated, for ISS to review [0436; 0328 rule 1; OQ-134]. *Talent Portal* stays untranslated,
  // as `i9.section1` has it — it is the product's name.
  'finish.intro':
    'En los próximos minutos vas a recibir un correo de bienvenida. Este correo te invitará a iniciar sesión en nuestro Talent Portal.',
  'finish.taskGroupsLabel': 'Hay tres grupos de tareas que vas a completar —',
  'finish.taskGroup1':
    'Tu perfil: esta es tu solicitud completa para trabajar con Integrity Staffing.',
  'finish.taskGroup2':
    'Nuestros dos primeros documentos de incorporación; luego, cuando estén completos,',
  'finish.taskGroup3': 'El siguiente grupo de documentos de incorporación.',
  'finish.cadence':
    'A medida que avanzas en cada paso, el siguiente grupo de tareas puede tardar aproximadamente 5-10 minutos en aparecer en tu perfil para que lo completes.',
  // ⭐ RECORDED_COPY — 0360's own Spanish, rendered AS RECORDED, never re-translated [0360; 0436 point 2].
  'finish.personalDevice':
    'El siguiente paso ocurre en tu propio teléfono — no en este kiosco. Toma asiento y espera tu correo de bienvenida.',
  'finish.iUnderstand': 'Entendido',
  'finish.handOff.heading': 'Toma asiento, por favor',
  'finish.handOff.body': 'Un reclutador te atenderá en breve.',
  'allDone.heading': 'Eso es todo por ahora',
  'allDone.body':
    'Toma asiento. Un representante te va a llamar por tu nombre en un rato para terminar el proceso.',
  'allDone.done': 'Listo',

  /*
   * The heading and the body are the sentences the closeout already carries in Spanish (`allDone.*`), which
   * is the same promise in the same words — kept identical rather than re-translated, so one screen does
   * not address the candidate in one register and the next in another. The register for the whole column is
   * **neutral «tú»** [Operator ruling, 2026-09-15 — see the note above `const es`], which is why these two
   * no longer read as voseo. The prototype's own `data-es` is written in «usted», which that ruling
   * supersedes as well, so it is not the source for these strings either.
   */
  'drugTest.heading': 'Toma asiento, por favor',
  'drugTest.body':
    'Un representante te va a llamar por tu nombre en un momento para tu prueba antidopaje.',
  'drugTest.callout':
    'Es una prueba de saliva sencilla. No comas nada ni mastiques chicle antes — puedes tomar agua si quieres.',

  /*
   * `11 · Why a pay card`. ⛔ **THE DESIGN FILE HAS NO SPANISH FRAME — not for this screen and not for any
   * screen, at any width.** So there is no source to transcribe the way the English above was, and 0026
   * still obliges both languages for every active string: an English key without its Spanish counterpart
   * renders a broken screen in front of a Spanish-speaking candidate at a hiring event.
   *
   * The Spanish below is therefore **the app's own**, in the neutral «tú» this dictionary uses, and it
   * is **owed the same 0040 confirmation as the English**. That is not a new position taken here — it is
   * the rule already recorded three keys down on `i9.assistQuestion` and again on `drugTest.*`: *0040
   * reserves the ENGLISH copy to ISS and 0026 obliges both languages, so the Spanish is the app's own and
   * travels in the same commit.*
   *
   * **Not lifted from the prototype's `paycard-why`, which does carry `data-es`**, and the reasons are
   * worth keeping because the prototype looks like the obvious source:
   *   · its English matches the frame's on only four of these eight strings, so the other four would be a
   *     Spanish sentence translating an English one nobody is shipping;
   *   · its step 2 — «el código de barras que viene en el paquete» — tells a Spanish-speaking candidate to
   *     open a packet 0164 rules they never open;
   *   · it is written in «usted», and mixing four formal strings into a «tú» screen addresses one
   *     candidate two ways on one page. `drugTest.*` records the same refusal for the same reason.
   *
   * ✅ «a la cámara», as the English says «the camera» — decision 0425 rules the word in both languages
   * (*cámara*, never *escáner*), and quotes this sentence: the earlier string with «al escáner» replaced.
   * ISS reviews the Spanish [0328 rule 1] → OQ-95.
   */
  'payCardWhy.heading': 'Cómo vas a cobrar',
  'payCardWhy.intro':
    'Los pagos electrónicos son la forma más confiable de asegurar que cobres a tiempo. Todas las personas que van a empezar una asignación con Integrity reciben una tarjeta de pago ADP, tengan pensado o no anotarse en depósito directo. Así, si alguna vez hay un problema con tu cuenta personal, podemos mandar tu pago a tu tarjeta ADP. Configurémosla ahora, así queda lista.',
  'payCardWhy.step1': 'Toma un paquete de tarjeta de la caja que está al lado de este kiosco.',
  'payCardWhy.step2':
    'Acerca a la cámara el código de barras que está en la parte de afuera del paquete — no hace falta que lo abras.',
  'payCardWhy.step3': 'Fíjate que el número que leemos coincida con el del paquete.',
  'payCardWhy.keepCard':
    'Quédate con la tarjeta — es tuya. Solo guardamos un ID proxy seguro, nunca el número de la tarjeta.',
  'payCardWhy.notYet':
    'No registres ni actives la tarjeta todavía — espera hasta tu primer día de pago. Vincularla acá es todo lo que tienes que hacer hoy.',
  'payCardWhy.continue': 'Continuar',

  /*
   * `13 · Pay card linked` / `R3 · Pay card linked`. ⛔ **THE DESIGN FILE HAS NO SPANISH FRAME — not for
   * this screen and not for any screen, at any width.** So there is nothing to transcribe the way the
   * English was, and 0026 still obliges both languages for every active string.
   *
   * The Spanish below is therefore **the app's own**, in the neutral «tú» this dictionary uses, and it
   * is **owed the same 0040 confirmation as the English**. That is the rule already recorded on
   * `i9.assistQuestion`, on `drugTest.*` and — three keys up, in the same feature and by the same
   * reasoning — on `payCardWhy.*`, which this follows rather than restating.
   *
   * **Not lifted from the prototype's `paycard-linked`, which does carry `data-es`.** Its English matches
   * the frame's word for word here, so the objection is only the register: it is written in «usted»
   * («Su tarjeta de pago está vinculada», «Guárdela en un lugar seguro»), and mixing four formal strings
   * into a «tú» flow addresses one candidate two ways on one page. `payCardWhy.*` and `drugTest.*` record
   * the same refusal for the same reason.
   *
   * ✅ `payCardLinked.activate` says «activar», never «registrar» — decision 0424, which quotes this
   * sentence: the earlier string with only the verb replaced. ISS reviews the Spanish [0328 rule 1] → OQ-134.
   */
  'payCardLinked.heading': 'Tu tarjeta de pago quedó vinculada',
  'payCardLinked.body':
    'Ya está en tu perfil, así que tu pago va a ir a esta tarjeta. Guárdala en un lugar seguro — no te van a dar otra.',
  'payCardLinked.activate':
    'En tu primer día de pago vas a poder activar la tarjeta y crear un PIN desde tu Talent Portal, en tu propio teléfono. No hay nada que hacer antes.',
  'payCardLinked.continue': 'Continuar',

  // KPC-1a · R1. The app's own Spanish (machine-translated, ISS reviews it [0328 rule 1]), in the app's
  // `tú` register — the prototype's `data-es` uses `usted`, which no other string here does.
  'payCardAsk.question': '¿Quieres vincular una tarjeta de pago nueva?',
  'payCardAsk.helper': 'Si tienes una tarjeta contigo, podemos vincularla ahora.',
  'payCardAsk.linkNew': 'Vincular una tarjeta de pago nueva',

  // E7. The app's own Spanish, as for R1 above (machine-translated, ISS reviews it [0328 rule 1]), in `tú`.
  'payCardNeed.eyebrow': 'TARJETA DE PAGO',
  'payCardNeed.question': '¿Necesitas una tarjeta de pago nueva?',
  'payCardNeed.helper': 'Si ya tienes una, ya está todo listo — no hay nada que hacer aquí.',
  'payCardNeed.yes': 'Sí, necesito una nueva',

  'payCard.needHelp': 'Pedir ayuda',
  'payCard.confirmHint':
    'Escríbelo de nuevo — copiar y pegar está deshabilitado para que no se cuele un error de tipeo (equivocarse significa que no cobrarías).',
  'payCard.orTypeItIn': 'O ESCRÍBELO',
  'payCard.intro':
    'Escanea el código de barras, o escribe el ID dos veces. Eso es todo lo que guardamos — nunca el número de la tarjeta.',
  // Decision 0490's approved Spanish, with its English counterpart (KPC-1d). ISS reviews it [0328 rule 1].
  'payCard.securityFraming':
    'Solo leemos el código de barras del paquete. No se graba ni se guarda nada de la cámara.',
  // ✅ 0425 point 3, with its English counterpart. 0425 leaves this Spanish to the app (machine-translated,
  // ISS reviews it [0328 rule 1]); it is step 2's Spanish, which 0425 quotes, without the trailing clause —
  // so the two screens name the camera and the packet in the same words. → OQ-95.
  'payCard.scanHint':
    'Acerca a la cámara el código de barras que está en la parte de afuera del paquete.',
  'payCard.scanTitle': 'Escanea tu tarjeta de pago',
  'payCard.idLabel': 'ID de la tarjeta',
  'payCard.idConfirmLabel': 'Confirma el ID',
  'payCard.save': 'Guardar y continuar',
  'payCard.mismatch': 'Los dos ID no coinciden. Revisa el número y escríbelo de nuevo.',
  'payCard.badFormat': 'El ID de la tarjeta tiene 16 dígitos. Revisa la tarjeta e ingresa los 16.',
  'payCard.rejected': 'Ese ID no parece correcto. Revisa la tarjeta e intenta de nuevo.',
  // The failed read, with its English counterpart. No frame draws Spanish: machine-translated kiosk copy in
  // neutral «tú», ISS reviews it [0328 rule 1; 0254]. «Pedir ayuda» is `payCard.needHelp`'s Spanish.
  'payCard.failedRead.title': 'No pudimos leer la tarjeta.',
  'payCard.failedRead.body':
    'Intenta escanear de nuevo — sostén el código de barras plano, sin taparlo con los dedos. Si sigue sin leerse, usa Pedir ayuda abajo.',
  'payCard.scan.title': 'Escanea tu tarjeta de pago',
  'payCard.scan.starting': 'Encendiendo la cámara…',
  // ⚠️ The scanning overlay's prompt, NOT the `12 · Scan` sentence (`payCard.scanHint`, 0425 point 3).
  // Still INTERIM, with its English counterpart; see the note there. → OQ-95.
  'payCard.scan.instruction':
    'Acerca el paquete cerrado a la cámara, con el código de barras dentro del recuadro punteado. Mantenlo plano y quieto.',
  'payCard.scan.found': 'Tarjeta leída. Revisa el número de abajo antes de continuar.',
  'payCard.scan.denied':
    'No se permitió el acceso a la cámara. Puedes escribir el número a mano, o un representante lo hace por ti.',
  'payCard.scan.unsupported':
    'Este equipo no puede leer el código por sí solo. Escribe el número a mano, o un representante lo hace por ti.',
  'payCard.scan.unavailable':
    'La cámara no se abrió. Escribe el número a mano, o un representante lo hace por ti.',
  'payCard.scan.videoLabel': 'Vista de la cámara para escanear tu tarjeta de pago',
  'payCard.scan.use': 'Usar esta tarjeta',
  'payCard.scan.again': 'Escanear de nuevo',
  'payCard.scan.typeInstead': 'Escribir el número a mano',
  'payCard.scan.recruiter': 'Pedir ayuda a un representante',

  /*
   * ───────────────────────────────────────────────────────────────────────────────────────────────────
   * `help.overlay.*` — the *Need help?* overlay, travelling with its English in the same change [0026].
   *
   * Machine-translated kiosk chrome under [0328] rule 1, in *español neutro* with «tú» [0254]; ISS reviews
   * it in the chrome review (OQ-134) [0441 point 3]. ⭐ **One exception: «Anónimo» is the Operator's own
   * word** (2026-10-07) [0441 § *The box*] — not machine output; do not reword it.
   * ───────────────────────────────────────────────────────────────────────────────────────────────────
   */
  'help.overlay.avatar': 'IA',
  'help.overlay.support': 'Soporte · siempre disponible',
  'help.overlay.close': 'Cerrar',
  'help.overlay.launch.heading': '¿Necesitas ayuda?',
  'help.overlay.launch.body':
    'Le avisaremos a un reclutador que estás aquí. Puedes seguir avanzando mientras esperas.',
  'help.overlay.launch.submit': 'Hablar con un reclutador',
  'help.overlay.name.heading': '¿Cómo te llamas?',
  'help.overlay.name.noRecord':
    'Todavía no tenemos un registro tuyo, así que un reclutador no sabrá a quién buscar. Dinos tu nombre y te agregaremos a la fila.',
  'help.overlay.name.label': 'Nombre',
  'help.overlay.name.placeholder': 'p. ej., María A.',
  'help.overlay.name.hint': 'Basta con tu nombre y la inicial de tu apellido.',
  'help.overlay.name.error': 'Escribe un nombre para que el reclutador sepa a quién buscar.',
  'help.overlay.name.submit': 'Agrégame a la fila',
  'help.overlay.queue.heading': 'Estás en la fila del reclutador',
  'help.overlay.queue.boxHeading': 'LO QUE VE EL RECLUTADOR',
  'help.overlay.queue.name': 'Nombre',
  'help.overlay.queue.site': 'Sitio',
  'help.overlay.queue.anonymous': 'Anónimo',
  'help.overlay.queue.footnote':
    'No es una alerta genérica: quien te atienda ya tiene esta información, así que no tendrás que repetirla.',
  'help.overlay.stillOpen':
    'Tu solicitud sigue abierta: al cerrar esto vuelves justo donde estabas.',
};

const DICTIONARIES: Record<Language, EnDictionary> = { en, es };

/**
 * The ONLY place in the app that indexes a dictionary.
 *
 * `noUncheckedIndexedAccess` makes the lookup `string | undefined`, which is deliberate — this function owns
 * the single fallback so no caller has to think about a missing key. Falling back to English rather than to
 * the raw key means a candidate reads real words even when a translation is genuinely missing at runtime.
 *
 * Complexity: O(1).
 */
/**
 * Values substituted into a translated string, by placeholder name.
 *
 * Numbers are accepted and formatted by the LANGUAGE, not by `String()` — a count of 1,000 applications is
 * `1,000` in English and `1.000` in Spanish, and doing it here is what keeps that out of the components.
 */
export type TranslationValues = Readonly<Record<string, string | number>>;

/**
 * Replaces `{name}` placeholders.
 *
 * Whole sentences stay in the dictionary rather than being assembled from fragments in a component. That is
 * not tidiness: Spanish and English order the parts of "Jobs near your ZIP (18101) — closest first."
 * differently, so a component that concatenated pieces would produce a correct English sentence and a
 * mis-ordered Spanish one — and the Spanish one is the half nobody on the team would notice.
 *
 * A placeholder with no value is left in place rather than blanked. `{zip}` on a lobby screen is a visible
 * defect somebody reports; an empty gap reads as intentional.
 */
function interpolate(template: string, values: TranslationValues, language: Language): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    if (value === undefined) return match;
    return typeof value === 'number' ? value.toLocaleString(LOCALE[language]) : value;
  });
}

/** The locale each language formats numbers in. Not the language tag itself — `es` alone picks a default. */
const LOCALE: Readonly<Record<Language, string>> = {
  en: 'en-US',
  es: 'es-US',
};

export function translate(
  language: Language,
  key: TranslationKey,
  values?: TranslationValues,
): string {
  const dictionary = DICTIONARIES[language];
  const template = dictionary[key] ?? en[key];
  return values === undefined ? template : interpolate(template, values, language);
}

/** The keys present in English — the authoritative key set. Used by the parity test. */
export function translationKeys(): readonly TranslationKey[] {
  return Object.keys(en) as TranslationKey[];
}

/** The dictionary for a language, for tests and tooling. Not for rendering — use `translate`. */
export function dictionaryFor(language: Language): EnDictionary {
  return DICTIONARIES[language];
}
